/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by cmiller
 */

// import {Dyn} from '../dynDataSet/dynMiscellany';
// import mediator from '../dynDataSet/mediatorUtil';
import { hasMathJaxPattern } from '../../utils/Util';

export const wrapDynAxisView = function(Dyn) {
	let mediator = Dyn.mediator;

	class NonAttributeScale {
	    // ensure that our non-attribute scale is off or Frequency, Percent, or Proportion Scale
	    type = "";

        hasFrequencyScale = () => { return this.type==="F"; }
        setFrequencyScale = (on) => {
            if( on || this.hasFrequencyScale() ){
                this.type = (on ? "F" : "");
            }
        }

        hasPercentScale = () => { return this.type==="%"; }
        setPercentScale = (on) => {
            if( on || this.hasPercentScale() ){
                this.type = (on ? "%" : "");
            }
        }

        hasProportionScale = () => { return this.type==="P"; }
        setProportionScale = (on) => {
            if( on || this.hasProportionScale() ){
                this.type = (on ? "P" : "");
            }
        }

        hasAnyScale() {
            return this.type.length > 0; // has any of F, %, P
        }

        save() {
            return this.type;
        }
        restore( savedType ) {
            this.type = String( savedType );
        }

        /**
         * returns the axis type for AxisView, compatible with Dyn.CombinedAttributes.getAxisType()
         * @returns {number} - enumeration 0=null, 1=numeric, 2=categorical
         */
        getAxisType() {
            return this.hasAnyScale() ? 1 : 0;
        }
    }

class AxisView {

    /** construct the left (Y-axis) or bottom (X-axis) view area of a plot */
    constructor( wantHorizontal, w, h, plotPaper ) {

        // layout
        this.bounds = new Dyn.mathUtil.Rect(0, 0, w, h);
        this.titleBounds = null;
        this.isHorizontal = wantHorizontal; //Boolean, true if horizontal layout (X-axis), otherwise Verical (Y-axis)
        this.axisID = wantHorizontal ? 'x':'y'; // derived classes change ID

        // data
        this.titleString = ""; // full string, this.title maybe elided
        this.titleMargin = Dyn.plotUtil.kAxisTitleMargin; // extra bottom/left space below title, blank or for extra attribute drop zone
        this.titleXHeight = 0; // line height of title text, 'X' height plus spacer
        this.type = 0;	// 0=null, 1=numeric, 2=categorical
        this.allAttributes = new Dyn.CombinedAttributes( this.isHorizontal ); // reference to 1st and additional attributes and combined statistics
        this.coordMap = null; // Dyn.mathUtil.CoordMap, maps data value to axis position
        this.numeric = {}; // properties: dataRange, userRange, axisRange, intervals={}
        this.maxAxisThickness = 0; // don't take up more than this much of parent view's space.
        this.tickThickness = 0;     // space reserved for ticks in cross axis (x for y-axis, y for x-axis)
        this.labelThickness	= 0;	// space reserved for tick labels in cross axis (x for y-axis, y for x-axis)
        this.labelInfo = new Dyn.AxisLabelUtil( this.isHorizontal ); // utility to calculate label rotation and spacing
        this.margin = 0; // extra top/right space to prevent plotted case icons from overlapping axis lines.
        this.marginForSum = 0; // extra top/right space to draw count or sum of stacked bars
        this.marginForButtons = 0; // extra space above Y-axis label to avoid overlap with map buttons
        this.secondAxis = null; // reference to Y2 axis if any for dual axis plots
        this.viewRefs = null; // reference to data in other subviews of the plot: class AxisViewRefs

        // axis options and flags
        this.isHiddenLineLabelsTicks = false;
        this.isUsingPromptTitle = false; // axis title is for "drop attribute here", not for an already-dropped attribute name.
        this.isUsingPlusMinus = false;
        this.isInDrag = false; // true when dragging is active
        this.isIncompleteAxisStyle = false; // show orange alert to indicate required attributes are missing?
        this.axisScale = new NonAttributeScale(); // see also isFrequencyAxis(), isPercentAxis(), isProportionalAxis()
        this.showGridLines = false; // if true, numeric grid lines are drawn instead of numeric tick marks
        this.showBinLines = false; // if true and categorical axis, draw lines between bins
        this.needsCalcAxis = false; // flag for lazy evaluation of axis options, see postCalcAxisUpdate()
        this.needsSetTitle = false; // flag for lazy evaluation of axis controls, see postCalcAxisUpdate().
        this.hideControls = false; // flag for when we are in Image Capture or View Only mode.
        this.needsRelayering = false; // flag to note that this.layerVisuals() needs to be called

        // Rafael elements
        this.paper = plotPaper;
        this.dropRect = null;
        this.extraDropRect = null; // for extra attributes if allowed
        this.extraDropText = null; // help text in extraDropRect
        this.titles = []; // { text, closeBox, colorRect }, one per attribute or one for optional prompt
        this.line = null; // numeric axis line, if any
        this.binLines = []; // lines between category bins, parallel to axis line
        this.ticks = [];  // tick marks/grid lines on axis (one per bin or numeric label)
        this.minorLines = []; // minor tick mark/grid lines (separated from .ticks for fast relayering)
        this.originLine = []; // origin tick mark or grid line (separated from .ticks for fast relayering)
        this.labelSelectRects = []; // rectangles for highlighting selected categories;
        this.labels = []; // labels on numeric ticks, or category bins
        this.plusButton = null;
        this.minusButton = null;
        this.dragMiddle = null; // middle drag control for numeric axis
        this.dragMin = null;
        this.dragMax = null;
        this.glowElement = null;
        this.settingsRect = null; // a.k.a. settings button for gear icon
    }

    destroy() {
	    this.removeAllAttributes(); // note: do first, may modify visuals
        this.removeVisuals();
    }

	/**
	 * Save the axis view to a data object that can be JSONified
	 * @returns {{}} - data to be used by restore()
	 */
	save() {
		let axisModel = {};

		if( this.allAttributes.count() > 0 ) {
			// array of attribute IDs, to get the Dyn.Attribute from the Dyn.dataSet
            axisModel.attributeID = this.allAttributes.attributes.map( attr => attr.id );
		} else {
			// else save the title string if any
			if( this.titleString ) {
				axisModel.titleString = this.titleString;
			} else {
                axisModel.titleString = "";
            }
		}
		if( this.numeric.userRange ) {
		    // save user-specified numeric axis adjustments
		    axisModel.userRangeMin = this.numeric.userRange.min;
		    axisModel.userRangeMax = this.numeric.userRange.max;
        }
        if( this.isUsingPromptTitle ) {
            axisModel.isUsingPromptTitle = this.isUsingPromptTitle;
        }
        if( this.axisScale.hasAnyScale() ) {
            axisModel.axisScale = this.axisScale.save();
        }
		return axisModel;
	}

	/**
	 * Change our axis view to match the saved data.
	 */
	restore( savedData ) {
        let titleString = "";

        // remove current attributes
        this.allAttributes.attributes.forEach( (attr) => { // notify views
            mediator.publish( "records:setRemovedAttribute", attr );
        });
        this.allAttributes.clearAttributes();

        // add new attributes
        if( savedData.allAttributeIDs ) {
            // old way: restore all attributes in array, for backward compatibility
            savedData.allAttributeIDs.forEach( id => {
                // note: if getAttributeByID() returns null, attribute will not be added to array
                this.allAttributes.addAttribute( Dyn.dataSet.getAttributeByID( id ) );
            } );
            titleString = this.allAttributes.name;
            this.isUsingPromptTitle = false;
        } else if( savedData.attributeID ) {
        	if (typeof( savedData.attributeID ) == "object") {
                // new way: attribute IDs (1+) are in an array
                savedData.attributeID.forEach( id => {
	                this.allAttributes.addAttribute( Dyn.dataSet.getAttributeByID( id ) );
	            } );
        	} else {
            	// old way: restore a single attribute, for backward compatibility
            	this.allAttributes.addAttribute( Dyn.dataSet.getAttributeByID( savedData.attributeID ) );
        	}
        	titleString = this.allAttributes.name;
            this.isUsingPromptTitle = false;
        } else {
            // restore title string with no attribute
            if( savedData.titleString ) {
                titleString = savedData.titleString;
            } else {
                titleString = "";
            }
            // #1335 clear default "PromptTitle" on restore unless savedData.isUsingPromptTitle === true
            this.isUsingPromptTitle = Boolean( savedData.isUsingPromptTitle );
            Dyn.assert( this.isUsingPromptTitle ? (titleString!=="") : true, "isUsingPromptTitle does not have an accompanying title with it");
        }
        this.axisScale.restore( savedData.axisScale || "" );

        this.setTitle( titleString ); // Note: saved string already translated with Dyn.loc()

        //  restore custom numeric range if any
        this.numeric = {}; // clear old numeric values including userRange
        if( Dyn.mathUtil.isFinite(savedData.userRangeMin)) {
            this.setUserCustomizedMinMax( new Dyn.mathUtil.Range( savedData.userRangeMin, savedData.userRangeMax ));
        }

		this.updateAxisTypeAndThickness();  // attribute type changes thickness
		// caller will do updatePlotView() to do the rest.
	}

	setFocus() {
		if (this.titles) {
			this.titles[0].closeBox[0].node.focus();
		}
	}

	isNumericAxis() {
		// axis style can be Numeric, Categorical, or Null (no attribute, or inactive attribute)
        this.checkStatsPending(); // this.type is unreliable if pending
		return( this.type === 1 );
	}


    isNumericActiveOrInactive() {
        // do we have 1+ numeric attributes on the axis, whether active or inactive?
        // to be used in plot update before end of updateAxes(), which is before attribute status is updated.
        // After updateAxes() call, use isNumericAxis() instead. #1434
        let attr1 = this.getAttribute();
        return attr1 ? (attr1.getDataType() === Dyn.dataUtil.types.kNumerical) : false;
    }

	isNumberLineAxis() {
    	// return true if we have a numeric axis with an active numberline (not binned or disabled or hidden line)
        this.checkStatsPending(); // this.type unreliable if pending
		var numActiveAttributes = this.allAttributes.countActive();
    	return(( this.type === 1 ) && (numActiveAttributes > 0) && (! this.isHiddenLineLabelsTicks));
	}

	isNumericalActiveAxis() {
		// return true if we have a numeric axis with 1+ active attributes (numberline may be hidden)
        this.checkStatsPending(); // this.type unreliable if pending
		var numActiveAttributes = this.allAttributes.countActive();
		return(( this.type === 1 ) && (numActiveAttributes > 0));
	}

    isNumericalScaleAxis() {
	    // are we showing a numberline, with or without a numeric attribute?
	    return this.isNumberLineAxis() || this.isFrequencyAxis() || this.isPercentAxis() || this.isProportionAxis();
    }

	isCategoricalAxis() {
		// axis style can be Numeric, Categorical, or Null (no attribute)
		// note: visuals and data settings may or may not have been updated to match attribute
        this.checkStatsPending(); // this.type is unreliable if pending
		return( this.type === 2 );
	}

    isCategoricalActiveOrInactive() {
        // do we have 1+ categorical attributes on the axis, whether active or inactive?
        // to be used in plot update before end of updateAxes(), which is before attribute status is updated.
        // After updateAxes() call, use isCategoricalAxis() instead. #1434
        let attr1 = this.getAttribute();
        return attr1 ? (attr1.getDataType() === Dyn.dataUtil.types.kCategorical) : false;
    }

    isCategoricalActiveAxis() {
        // return true if we have a categorical axis with 1+ active attributes
        this.checkStatsPending(); // this.type is unreliable if pending
        var numActiveAttributes = this.allAttributes.countActive();
        return(( this.type === 2 ) && (numActiveAttributes > 0));
    }

	isNullAxis() {
		// a null axis is one with no active attributes (can have inactive attributes)
        // see also, isNumeric(), isCategorical(), isEmptyOfAttributes()
		Dyn.assert( this.getAttribute() === null || this.getAttribute() instanceof Dyn.Attribute );
        this.checkStatsPending(); // this.type is unreliable if pending
		return( this.type === 0 );
	}

    isFrequencyAxis() {
	    // are we showing a numeric "Frequency" scale set by the plot via setPlotSpecificMinMax()?
        // if true, all attributes on the axis should be inactive and hidden
        this.checkStatsPending(); // this.type is unreliable if pending
        return( Boolean(this.axisScale.hasFrequencyScale() &&( ( this.type === 0 ) || (this.allAttributes.countActive()===0))));
    }

    isPercentAxis() {
        // are we showing a numeric "Percent" scale set by the plot via setPlotSpecificMinMax()?
        // if true, all attributes on the axis, if any, should be numeric
        this.checkStatsPending(); // this.type is unreliable if pending
        return( Boolean(this.axisScale.hasPercentScale() && (
            (this.type === 0) || // percent scale on null axis (percent of case frequencies)
            (this.type === 1) || // percent scale on numeric axis (percent of case values)
            ((this.type === 2) && this.allAttributes.countActive()===0)))); // percent scale with inactive categorical attribute
    }

    isProportionAxis() {
        // are we showing a numeric "Proportion" scale set by the plot via setPlotSpecificMinMax()?
        // if true, all attributes on the axis, if any, should be numeric
        this.checkStatsPending(); // this.type is unreliable if pending
        return( Boolean(this.axisScale.hasProportionScale() && (
            (this.type === 0) || // percent scale on null axis (percent of case frequencies)
            (this.type === 1) || // percent scale on numeric axis (percent of case values)
            ((this.type === 2) && this.allAttributes.countActive()===0)))); // percent scale with inactive categorical attribute
    }

    isNonAttributeScaleAxis() {
	    // is showing a working Frequency, Percent, or Proportion axis scale
	    return this.isFrequencyAxis() || this.isPercentAxis() || this.isProportionAxis();
    }

	isEmptyOfAttributes() {
		// return true if axis has no dataset attribute (enabled or disabled)
		// Note that axis may be of any type (null, prompt, numeric, categorical), with or without Frequency/Percent/Proportion scale.
        // Can be called in plot update before end of updateAxes(), which is before attribute status is updated.
        // See also isNull(), isIncompleteAxis() #1434
		return this.allAttributes.count()===0;
	}

	isPromptAxis() {
		// return true the axis contains a non-empty title but no attribute definition.
		// we assume that this indicates a prompt for the user to drop attributes on the axis.
		return Boolean( this.titleString && this.allAttributes.count()===0 );
	}

    // experimental feature: if true, and axis is incomplete even with an attribute on the plot,
    // show the orange "alert" style (e.g. if true, then show Gender attribute on X axis of map plot,
    // else show Gender as a disabled attribute only -- CDM 2020-09-14
    //DEBUG_showIncompleteStyleWithWrongAttributeType = false;

    isIncompleteAxis() {
	    // return true if we have a prompt axis (title but no attribute), or disabled attribute(s),
        // and the axis in the "incomplete plot" style, indicating that a valid attribute is required.
        // see also .setIncompleteAxis()
        this.checkStatsPending(); // this.isIncompleteAxisStyle is unreliable if pending
	    return Boolean( this.isIncompleteAxisStyle &&(
            /*this.DEBUG_showIncompleteStyleWithWrongAttributeType ||*/
            this.isPromptAxis() || (this.allAttributes.countActive()===0)));
    }

	/*
	 * Checks if axis has attribute with attributeID
	 * return type: boolean
	 */
	isAttributeOnAxis(attributeID) {
		var hasAttribute = false;
		if( this.allAttributes.attributes.find( attr => attr.id === attributeID )) {
		    hasAttribute = true;
		}
		return hasAttribute;
	}

	/**
	 * Initialize the visible parts of the axis.  To be called when we have the initial view size for this axis.
     * See also updateVisuals()
	 * @param {number} maxAxisThickness
	 * @param {Function} mouseOverHandler
	 * @param {Function} mouseOutHandler
	 * @param {Function} mouseUpHandler
	 */
	initVisuals( maxAxisThickness, mouseOverHandler, mouseOutHandler, mouseUpHandler ) {
		// get correct initial setting, then only change in setViewMode(), for Image Capture or View Only mode.
        this.hideControls = Dyn.preferences.isViewOnly();
        Dyn.assert( this.labels.length === 0, "labels should be empty" );

		// create the drop zone rectangle
		//  and set up dropable zone handlers, register axis as potential target
		this.dropRect = this.paper.rect( this.bounds.x, this.bounds.y, this.bounds.width, this.bounds.height, 0 ).
				attr({
					stroke: Dyn.plotUtil.promptDropStrokeColor,
					"stroke-width": Dyn.plotUtil.promptDropRectStrokeWidth
				});
		this.dropRect.node.setAttribute("isHorizontal", this.isHorizontal);
        this.dropRect.node.setAttribute("axisID", this.axisID);
        this.dropRect.node.setAttribute("class", Dyn.plotUtil.hideOnPreviewClass);
        this.dropRect.node.setAttribute("id", this.isHorizontal ? Dyn.plotUtil.xAxisDropAreaId: Dyn.plotUtil.yAxisDropAreaId);
		if(!Dyn.environment.isTouchDevice()) {
			this.dropRect.mouseover( mouseOverHandler );
			this.dropRect.touchstart( mouseOverHandler ); // TODO: check to see, does this do anything?
			//this.dropRect.touchmove( mouseOverHandler );
			this.dropRect.mouseout( mouseOutHandler );
			this.dropRect.mouseup( mouseUpHandler );
		}
		this.dropRect.data( "isExtraDropRect", 0 );
		this.dropRect.data( "tooltip", "Replace attribute(s)" );
		this.dropRect.click( this.clickBackground);
		// this.dropRect.mouseout(this.handleMouseOut);  mouseOutHandler does not exist

		// create a hidden extra drop rectangle for multiple attributes (not yet enabled)
		this.extraDropRect = this.paper.rect( this.bounds.x, this.bounds.y, 0, 0, 0 ).
			attr({ fill: Dyn.plotUtil.containerBackground, stroke: "none"/*, "stroke-width": Dyn.plotUtil.dropRectStrokeWidth*/ });
		this.extraDropRect.node.setAttribute("isHorizontal", this.isHorizontal);
        this.extraDropRect.node.setAttribute("axisID", this.axisID);
        this.extraDropRect.node.setAttribute("class", Dyn.plotUtil.hideOnPreviewClass);
        this.extraDropRect.node.setAttribute("id", this.isHorizontal ? Dyn.plotUtil.xAxisExtraDropAreaId : Dyn.plotUtil.yAxisExtraDropAreaId);
		if(!Dyn.environment.isTouchDevice()) {
			this.extraDropRect.mouseover( mouseOverHandler );
			//this.extraDropRect.touchstart( mouseOverHandler );
			this.extraDropRect.mouseout( mouseOutHandler );
			this.extraDropRect.mouseup( mouseUpHandler );
		}
		this.extraDropRect.data( "isExtraDropRect", 1 );
		this.extraDropRect.data( "tooltip", Dyn.loc("Add another attribute"));
		this.dropRect.click( this.clickBackground );

		this.extraDropText = this.paper.text( 0, 0, "+" ).hide();
		this.extraDropText.attr({
            "font-size":Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisTitleFontSize ),
            fill:Dyn.plotUtil.titleColor,
            class: 'tde-attribute-name ' + Dyn.plotUtil.hideOnPreviewClass });
		Dyn.plotUtil.disableMouseOverEvents( this.extraDropText ); // don't interfere with mouse over extraDropRect

		this.maxAxisThickness = maxAxisThickness;

        this.titleBounds = new Dyn.mathUtil.Rect();
        this.updateTitleXHeight();

        // create and hide optional buttons
		this.plusButton  = Dyn.axisUtil.makePlusMinusButton( this, "plusButton",  Dyn.loc("increase number of bins"), this.isHorizontal ? Dyn.plotUtil.xAxisPlusButtonId : Dyn.plotUtil.yAxisPlusButtonId);
        this.minusButton = Dyn.axisUtil.makePlusMinusButton( this, "minusButton", Dyn.loc("decrease number of bins"), this.isHorizontal ? Dyn.plotUtil.xAxisMinusButtonId : Dyn.plotUtil.yAxisMinusButtonId);
		this.dragMiddle  = Dyn.axisUtil.makeDragButton( this, "dragMiddle", Dyn.loc("axis range"), this.isHorizontal ? Dyn.plotUtil.xAxisDragMiddleButtonId : Dyn.plotUtil.yAxisDragMiddleButtonId);
		this.dragMin     = Dyn.axisUtil.makeDragButton( this, "dragMin", Dyn.loc("axis minimum"), this.isHorizontal ? Dyn.plotUtil.xAxisDragMinButtonId : Dyn.plotUtil.yAxisDragMinButtonId);
        this.dragMax     = Dyn.axisUtil.makeDragButton( this, "dragMax", Dyn.loc("axis maximum"), this.isHorizontal ? Dyn.plotUtil.xAxisDragMaxButtonId : Dyn.plotUtil.yAxisDragMaxButtonId);
        this.settingsRect = Dyn.axisUtil.makeSettingsButton( this, "settingsButton", Dyn.loc("axis settings"), this.isHorizontal ? Dyn.plotUtil.xAxisSettingsButtonId : Dyn.plotUtil.yAxisSettingsButtonId);
	}

    /**
     * Q:Do we show the extra drop rectangle for multiple attributes?
     * A: if this.allowAnotherAttribute() returns true
     *
     * Q:Do we allow this particular attribute to be added to the existing attributes?
     * A: if this.allowAnotherAttribute( anAttribute ) returns true
     *
     * @param optionalNewAttribute
     * @returns {*|boolean}
     */
	allowAnotherAttribute( optionalNewAttribute, optionalChangedAttribute ) {
		"use strict";
		// do we keep the existing attribute and allow the new attribute as an extra (2nd, 3rd, etc) attribute?
		// if new attribute is null, we ignore the extra tests for a new attribute
        this.checkStatsPending();
		var hasAllNumericAttributes = this.isNumberLineAxis(),
			newAttributeOK = true;
		if( optionalNewAttribute ) {
			var isNewAttributeForAxis = (this.allAttributes.findAttribute( optionalNewAttribute ) < 0),
				isNumericAttribute = optionalNewAttribute.stats.isNumeric();
			newAttributeOK = isNewAttributeForAxis && isNumericAttribute;
		} else if( optionalChangedAttribute ) {
		    newAttributeOK = optionalChangedAttribute.stats.isNumeric();
        }
		return hasAllNumericAttributes && newAttributeOK;
	}

	/**
	 * change this axis after a new attribute is dropped
     * see also droppedAttributesUpdate()
	 * @param newAttribute {Dyn.Attribute}
	 * @param wantMultipleAttributes {boolean} true if we want to keep existing attributes, false if we want to replacee existing attributes
	 * @returns {{viewSize: boolean, isHorizontal: boolean, action: string}}
	 */
	dropAttribute( newAttribute, wantMultipleAttributes ) {
		"use strict";
		//Dyn.time( 'axisView.dropAttribute', 'Start' );

		var whatChanged = { viewSize: true, attributeReset: true, isHorizontal: this.isHorizontal, axisID:this.axisID, action: "dropAttribute" },
			allowMultipleAttributes = wantMultipleAttributes && this.allowAnotherAttribute( newAttribute );

		if( ! allowMultipleAttributes ) { // if not adding as a 2nd+ attribute, then we clear the old attributes first.
            this.allAttributes.attributes.forEach( (attr) => { // notify views
                mediator.publish( "records:setRemovedAttribute", attr );
            });
			this.allAttributes.clearAttributes();
		}
		this.allAttributes.addAttribute( newAttribute );
		this.setTitle( this.allAttributes.name ); // set title from attribute name
		this.numeric = {}; // clear old numeric values including userRange
		this.updateAxisTypeAndThickness();  // attribute type changes thickness

		// don't change any visible view elements that animate or depend on view position adjustments
		// call this.updateAxisElements() later, after all view sizes adjusted
        //Dyn.time( 'axisView.dropAttribute', 'End' );
        this.resetHighlightDropAreas();
		return whatChanged;
	}

    /**
     * update this axis after changes to data or meta-data of attributes on our axis.
     * should exactly parallel changes made by dropAttribute() and removeAttribute()
     */
    droppedAttributesUpdate( changedAttribute ) {
        let hasMultipleAttributes = (this.allAttributes.count() > 1),
            allowMultipleAttributes = this.allowAnotherAttribute( null, changedAttribute );
        if( hasMultipleAttributes && ! allowMultipleAttributes ) {
            // remove all other attributes on this axis (i.e. only keep attribute changed from numeric to categorical)
            this.allAttributes.attributes.forEach( (attr) => { // notify views
                mediator.publish( "records:setRemovedAttribute", attr );
            });
            this.allAttributes.clearAttributes();
            this.allAttributes.addAttribute( changedAttribute );
        }
        this.allAttributes.updateStats();
        this.setTitle( this.allAttributes.name ); // set title from attribute name
        this.numeric = {}; // clear old numeric values including userRange
        this.updateAxisTypeAndThickness();  // attribute type changes thickness
        this.resetHighlightDropAreas();
    }

	/**
	 * removeAttribute() -- remove the attribute(s) on the axis
	 * @param {string} [newAxisTitle] --string or undefined (for blank title)
	 * @param {*} [ioWhatChanged] -- Dyn.EPlotChange flags for Dyn.PlotView.updatePlotView()
	 * @param {Dyn.Attribute} [optionalAttributeIndex] -- attribute index if want to remove only one of multiple attributes.
	 * @returns {{viewSize: boolean, isHorizontal: *}}
	 */
	removeAttribute( newAxisTitle, ioWhatChanged, optionalAttributeIndex ) {
        "use strict";
		var whatChanged = ioWhatChanged || { attributeReset: true, action: "removeAttribute" },
			hadAttribute = ! this.isEmptyOfAttributes();

		if( Dyn.dataUtil.isNumber( optionalAttributeIndex )) {
			// remove just the one attribute
			mediator.publish( "records:setRemovedAttribute", this.allAttributes.attributes[ optionalAttributeIndex] );
			this.allAttributes.removeAttribute( optionalAttributeIndex );
		} else {
			// remove all attributes if any
            this.allAttributes.attributes.forEach( (attr) => { // notify views
                mediator.publish( "records:setRemovedAttribute", attr );
            });
            this.allAttributes.clearAttributes();
		}
		this.setTitle( newAxisTitle || this.allAttributes.name );
		this.numeric = {}; // clear old numeric values including userRange
        this.updateAxisTypeAndThickness();  // attribute type changes thickness

		// don't change any visible view elements that animate or depend on view position adjustments
		// call this.updateAxisElements() later, after all view sizes adjusted
		if( hadAttribute )
			whatChanged.viewSize = true;
		return whatChanged;
	}

    removeAllAttributes() {
	    this.removeAttribute();
    }

	/**
	 * Reset user or plot customizations on this axis.
	 *
	 * To be used if we :
	 * 1. change plot types (reset both axes)
	 * or 2. add/remove/change data values of an attribute on the axis (reset both axes)
	 * or 3. or drop or remove an attribute on one axis (reset both axes).
	 * (but there may be types of data value changes where we don't want to reset the axis, needs some thought)
	 *
	 * Without Reset, axis scaling or categories might be out of date, since missing values or plot customizations
	 * from one attribute/axis might affect the other axis.
	 *
	 * See also DropAttribute() and ResetAttribute() which also do the same reset.
	 */
	axisCustomizationsReset() {
		"use strict";
		var hasCustomNumericValues =Boolean( this.numeric.userRange || this.numeric.plotRange );
		if( hasCustomNumericValues ) {
			this.numeric = {}; // clear old numeric values including userRange
			this.needsCalcAxis = true; // see postCalcAxisUpdate()
		}
	}

	/**
	 * Set up the axis title and optional controls next to the title.
	 * modifies:
	 * 	this.title
	 * 	this.titleString
	 * 	(+ controls updated by updateTitleButtons())
	 *
	 * @param {string} [optionalTitleString='']
     * @param {boolean}[isRequiredAxis]
	 */
	setTitle( optionalTitleString ) {
		"use strict";
		var titleString = Dyn.plotUtil.unescapeText( String( optionalTitleString ) ),
			isNonEmptyTitleString = ( titleString.length > 0 ),
            isEmptyAttribute = ( this.getAttribute() === null );

		// save full string for future reference, since displayed string may be elided.
		this.titleString = titleString;
        this.isUsingPromptTitle = isNonEmptyTitleString && isEmptyAttribute;
	}

    /**
     * Set the flag to change the axis style, indicating that attributes are needed.
     * often combined with the prompt axis style.  See .isIncompleteAxis(), .isPromptAxis().
     * @param isIncompleteAxis
     */
    setIncompleteAxis( isIncompleteAxis ) {
        this.isIncompleteAxisStyle = Boolean( isIncompleteAxis );
    }

	/**
	 * hover over a tick mark or category label
	 */
	hoverOverLabel( event ){
		"use strict";
		// send xy position of the element to display tool tip accordingly
		var isCategory = Boolean(this.data("category"));
		Dyn.toolTip.show( this, event, { x:this.attrs.x, y:this.attrs.y });
		if( isCategory ) {
			event.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
		}
	}

	/**
	 * Clean up the tooltip from tick mark or category labels
	 */
	hoverOutLabel() {
		"use strict";
		if( Dyn.toolTip.isMyElement( this ))
			Dyn.toolTip.hide();
	}

	/** handler for clicking on Attribute Category */
	clickCategory( event ) {
		"use strict";
		var attrID = this.data("attributeID" ),
			categoryName = this.data("category" ),
			wantAdd = Boolean( event && (event.shiftKey || event.ctrlKey || event.metaKey) ),
			data = { "attributeID":attrID, "category":categoryName, addToSelection:wantAdd };
		// select the cases with the given category for this attribute
		mediator.publish( "records:casesSelectedByCategory", data );
	}

	/** handler for clicking on axis background */
	clickBackground( event ) {
		"use strict";
		// reset category selection and case selection, don't toggle case card
		var deselectAllData = { ids: [], object: "plot", disableToggle: true };
		mediator.publish( "records:casesSelected", deselectAllData );
	}

    /** handler to start drag of one category (text or dot element===this) */
    startDragCategory( x, y, event ) {
        // drag event callback, this===dragged element created by createCategorySquareAndText()
        let dragInfo = this.data('dragInfo');
        if( dragInfo.isDraggable ) { // e.g. 2+ categories, not numeric intervals
            this.data('inDrag', true );
            this.data('v0', this.attr( dragInfo.coordName(this)));
            // change cursor while mouse down (then revert to pointer cursor)
            this.attr('cursor', dragInfo.isOnX ? Dyn.plotUtil.cursorHorizontalBothSide : Dyn.plotUtil.cursorVerticalBothSide );
        }
        Dyn.plotUtil.safeStopPropogation( event );
    }

    /** handler for mouse-up on drag of one category (text or dot element===this) */
    endDragCategory( event ) {
        // drag event callback, this===dragged element created by createCategorySquareAndText()
        if( this.data('inDrag') ) {
            let dragElement = this, // circle or text element from makeCategoricalAxis()
                dragInfo = this.data('dragInfo'),
                oldCoord = this.data( 'v0' ),
                newCoord = this.attr( dragInfo.coordName(this)),
                catIndex = this.data('catIndex'),
                newIndex = this.data('newIndex');
            if( dragInfo.isClickNotDrag( oldCoord, newCoord )) {
                // no dragging took place on mouse up; treat as a click to select the category
                dragInfo.view.clickCategory.call( this, event );
            }
            if( newIndex >= 0 && newIndex !== catIndex ) {
                // animate to final position
                dragInfo.view.moveCategoriesToDragPosition( oldCoord, catIndex, newIndex, dragInfo, true );
                // save changes and update all views
                setTimeout( ()=> {
                    mediator.publish( "moveCategoryOfAttribute", {
                        attributeID: this.data( 'attributeID' ),
                        oldIndex: catIndex,
                        newIndex: newIndex
                    })}, dragInfo.animationTimeMS ); // give time for animation to finish before updating views
            } else {
                // drag ended in about the same position, redraw to original pos. to clean up view
                dragInfo.view.moveCategoriesToDragPosition( oldCoord, catIndex, catIndex, dragInfo, true );
            }
            this.data( 'inDrag', false );
            // revert to pointer cursor, undoing startDragCategory() change
            this.attr('cursor', Dyn.plotUtil.cursorPointer );
        }
        Dyn.plotUtil.safeStopPropogation( event );
    }

    /** handler for mouse move on drag of one category (text or dot element===this) */
    dragMoveCategory( dx, dy, x, y, event ) {
        // drag event callback, this===dragged element created by createCategorySquareAndText()
        if( this.data('inDrag') ) {
            let dragInfo = this.data( 'dragInfo' ),
                deltaCoord = dragInfo.isOnX ? dx : dy,
                newCoord = this.data( 'v0' ) + deltaCoord,
                catIndex = this.data( 'catIndex' ),
                newIndex = dragInfo.getCategoryIndexFromDelta( catIndex, deltaCoord );
            this.data( 'newIndex', newIndex ); // save for dragEndCategory()
            dragInfo.view.moveCategoriesToDragPosition( newCoord, catIndex, newIndex, dragInfo );
            // TODO: set double-arrow cursor while mouse down, even if mouse not over the category label
            // event.currentTarget.style.cursor = dragInfo.isOnX ? Dyn.plotUtil.cursorHorizontalBothSide : Dyn.plotUtil.cursorVerticalBothSide;
        }
        Dyn.plotUtil.safeStopPropogation( event );
    }

    /** move the given attribute category to the new position,
     * and move the other categories in the legend out of the way.
     * @param v -- new position in x or y of dragged category
     * @param oldIndex -- original (pre-drag) index of dragged category (in list of the attribute's categories)
     * @param newIndex -- new index of dragged category
     * @param dragInfo {{Dyn.DragCategoryUtil}} -- drag information (shared by all categories)
     * @param finalAnimation {boolean} -- true if we want dragged category to animate to new position
     */
    moveCategoriesToDragPosition( v, oldIndex, newIndex, dragInfo, finalAnimation ) {
        let tcName = dragInfo.coordName(dragInfo.view.labels[0]), // text element coordinate name to change (x or y)
            ccName = dragInfo.coordName(dragInfo.view.ticks[0]), // circle element coordinate name to change (cx or cy)
            axisView = this;
        function setPos( el, attrName, pos, isAnimated ) {
            if( axisView.labelInfo.wantRotation ) {
                // because of bugs in positioning rotated text must unrotate first, and not animate
                let isText = (el.type=='text');
                if(isText) el.attr('transform','');
                el.attr( attrName, pos );
                if(isText) el.attr('transform', axisView.getLabelTextTransform( el ));
            } else {
                // else move or animate normally
                let attrs = {};
                attrs[attrName] = pos;
                if( isAnimated )
                    el.animate( attrs, dragInfo.animationTimeMS, dragInfo.animationEasing );
                else
                    el.attr( attrs );
            }
        }
        function calcAndSetPos( el, attrName, elOffset=0 ) {
            let ci = el.data('catIndex');
            if( ci === oldIndex && ! finalAnimation ) {
                // move visible category to the mouse position
                setPos( el, attrName, dragInfo.getLimitedDragPosition( v ));
            } else {
                // reposition other categories out of the way (leave a blank line where dragged category will go)
                let ci2 = dragInfo.getCategoryIndexWithSkipping( ci, oldIndex, newIndex );
                setPos( el, attrName, dragInfo.cat0Coord + (dragInfo.catOffset * ci2) + elOffset, true );
            }
        }

        // reposition the visible elements of each category label on the axis
        this.ticks.forEach( (circleEl) => { // circles for each category, even if text is hidden
            calcAndSetPos( circleEl, ccName /*, dragInfo.symbolOffset*/ );
        });
        this.labelSelectRects.forEach( (rectEl) => { // rectangles to highlight selected categories, if any
            calcAndSetPos( rectEl, tcName, rectEl.data('rectOffset') );
        });
        this.labels.forEach( (textEl) => { // text labels, some may be hidden, due to lack of space
            calcAndSetPos( textEl, tcName );
        });
    }

	setMargin( newMargin ) {
		"use strict";
		Dyn.assert( newMargin >= 0 && newMargin <= 1000, "invalid margin" );
		this.margin = newMargin;
		//console.log((this.isHorizontal ? "X":"Y")+" axis SetMargin( "+this.margin+")");
	}

    /**
     * Calculate the desired thickness of our axis, when the plot view paper size changes.
     * NOTE: Thickness is bounds.height for X-axis, bounds.width for Y-axis.
     * Here we only updates this one parameter of the bounds, so that parent view can dynamically place
     * all axes and boundaries together, via a later call to this.updateVisuals()
     * @returns {number} -- desired view height/width in pixels.
     */
    calcThickness() {
        "use strict";
        function getCurrentThickness( axisView ) { return( axisView.isHorizontal ? axisView.bounds.height : axisView.bounds.width ); }
        function getMaxThickness( axisView ) { return( Dyn.plotUtil.kAxisMaxProportion * (axisView.isHorizontal ? axisView.paper.height : axisView.paper.width )); }

        var currThickness = getCurrentThickness( this ),
            currMaxThickness = this.maxAxisThickness,
            currTitleXHeight = this.titleXHeight,
            newMinThickness = Dyn.plotUtil.kMinAxisThickness, // Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.kMinAxisThickness ),
            newMaxThickness = getMaxThickness( this ),
            newTitleXHeight = this.updateTitleXHeight();
        if( Dyn.plotUtil.isDrawOnly() ) {
            newMinThickness = newMaxThickness = 0; // hide axis if in draw-only mode
        }
        // if any of these conditions are true, we must recalculate our axis thickness
        let changeToMaxThickness = ((currThickness === currMaxThickness) && (newMaxThickness !== currMaxThickness)),
            changeBecauseOfClipping = (currThickness !== Dyn.mathUtil.clipToRange( currThickness, newMinThickness, newMaxThickness )),
            changeToTargetNumberOfTicks = this.isNumericAxis() && ( this.targetNumTicks() !== this.numeric.targetNumTicks),
            changeToTitleXHeight = (currTitleXHeight !== newTitleXHeight);
        this.maxAxisThickness = newMaxThickness;
        if( changeBecauseOfClipping || changeToMaxThickness || changeToTargetNumberOfTicks || changeToTitleXHeight ) {
            this.updateAxisTypeAndThickness(); // recalculate axis thickness including related parameters such as text rotation
        }
        return getCurrentThickness( this );
    }

	/**
	 * update the axis thickness, depending on the axis type (don't change anything else)
	 */
	updateAxisTypeAndThickness() {
		let axisMinPx = this.bounds.leftOrBottom( this.isHorizontal ),
            axisMaxPx = this.bounds.rightOrTop( this.isHorizontal ),
            axisWidth = Math.abs( axisMinPx - axisMaxPx ),
            labelFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisLabelFontSize );
        this.labelInfo = new Dyn.AxisLabelUtil( this.isHorizontal );

        // update axis type first, this affects marginSize and other calculations
        this.type = this.allAttributes.type || this.axisScale.getAxisType(); // enumeration 0=null, 1=numeric, 2=categorical
        this.updateTitleXHeight();
        let marginSize = this.allowAnotherAttribute() ?
                Dyn.plotUtil.extraDropZoneThickness + Dyn.plotUtil.extraDropZoneSpacer :
                Dyn.plotUtil.kAxisTitleMargin,
            numTitles = this.allAttributes.count() // count attribute titles, disabled or enabled
                + ( this.isNonAttributeScaleAxis() ? 1 : 0 )  // count Freq/Count/Proportion if on
                + ( this.isPromptAxis() ? 1 : 0 ); // count extra prompt if on (normally not present with other titles)

        // now calculate and set axis thickness according to axis type
		if( this.isHiddenLineLabelsTicks ) {
			this.numeric = {}; // clear old numeric values
			this.numeric.axisRange = new Dyn.mathUtil.Range( 0, 1 ); // replace with normalized mapping
			this.setAxisThickness( this.allAttributes.count(), marginSize, 0, 0 );

		} else if( this.isNumericalScaleAxis() ) {
            this._updateNumericAxisOptions(); // update labels
            // compare formatted numeric label sizes to available space to determine text rotation and space to use
            this.labelThickness = this.labelInfo.getLabelThicknessAndRotation( this.numeric.intervals.labels,
                this.allAttributes, this.paper, axisWidth, labelFontSize, this.kSpaceBetweenLabels, this.margin );
			this.setAxisThickness( numTitles, marginSize, this.labelThickness, AxisView.kTickLength );

		} else if( this.isCategoricalActiveAxis()) {
			this.numeric = {}; // clear old numeric values
			// compare label text sizes to available space to determine text rotation and space to use
			this.labelThickness = this.labelInfo.getLabelThicknessAndRotation( this.getAttribute().stats.categories,
                this.allAttributes, this.paper, axisWidth, labelFontSize, this.kSpaceBetweenLabels );
			this.setAxisThickness( numTitles, marginSize, this.labelThickness, AxisView.kTickLength );

		} else { // null axis
		    //let numTitles = this.isPromptAxis() ? 1 : 0;
			this.numeric = {}; // clear old numeric values
			this.setAxisThickness( numTitles, marginSize, 0, 0 );
		}
	}

    /** compute the numeric axis range and tick mark formatting */
    _updateNumericAxisOptions() {
        let n = this.numeric,
            formattingAttribute = this.getFormattingAttribute(),
            axisOptions = {
                formatString: formattingAttribute?.format?.formatString,
                exactWidth: formattingAttribute?.format?.axisMajorWidth, // optional major interval width from MetaData
                minorWidth: formattingAttribute?.format?.axisMinorWidth, // optional minor interval width from MetaData
            };

        // Calc axis numeric range
        // start with data min and max (with special cases for Frequency or Percent axis, or sampling mode)
        if( this.isNonAttributeScaleAxis() ) {
            // TODO: possibly consolidate this if/else statement, formattingAttribute is used in both branches
            n.dataRange = new Dyn.mathUtil.Range( formattingAttribute.stats.min, formattingAttribute.stats.max );
            axisOptions.subType = formattingAttribute.subType;
            axisOptions.maxDecimals = formattingAttribute.getNumericDecimals(); // restrict major tick marks to user preference
            axisOptions.maxDataDecimals = formattingAttribute.getNumericDecimals( undefined, true ); // restrict minor tick marks to data decimals
        } else {
            n.dataRange = this.allAttributes.getModifiedDataRange();
            axisOptions.subType = this.allAttributes.subType; // for special handling of date/time numeric types
            axisOptions.maxDecimals = this.allAttributes.getNumericDecimals(); // restrict major tick marks to user preference
            axisOptions.maxDataDecimals = this.allAttributes.getNumericDecimals( undefined, true ); // restrict minor tick marks to data decimals
        }
        // override with plot-specific min and max if it exists, then expand to nice intervals
        n.axisRange = new Dyn.mathUtil.Range(
            (n.plotRange ? n.plotRange.min : n.dataRange.min),
            (n.plotRange ? n.plotRange.max : n.dataRange.max) );
		n.defaultUserRange = this.allAttributes.getDefaultUserRange();
        if( n.userRange ) {
            // override data or plot range with user-specified min and max (e.g. from dragging)
            n.axisRange.set( n.userRange.min, n.userRange.max );
        } else if( n.defaultUserRange ) {
			n.axisRange.set( n.defaultUserRange.min, n.defaultUserRange.max );
		}
        if( n.userRange || n.defaultUserRange ) {
            axisOptions[Dyn.dataUtil.EIntervalRange.irLowerInside] = true;
            axisOptions[Dyn.dataUtil.EIntervalRange.irUpperInside] = true;
        } else {
            // default numeric axes have tick marks 'outside' of data (first tick <= data <= last tick).
            axisOptions[Dyn.dataUtil.EIntervalRange.irLowerOutside] = true;
            axisOptions[Dyn.dataUtil.EIntervalRange.irUpperOutside] = true;
        }

        // Calculate nice intervals
        n.axisRange.makeNonZeroWidth(); // handle range with of zero by making it a non-zero width
        n.targetNumTicks = this.targetNumTicks();
        n.intervals = Dyn.dataUtil.calcNiceIntervals( n.axisRange.min, n.axisRange.max, n.targetNumTicks, axisOptions );
        n.axisRange.set(
            (n.userRange ? n.userRange.min : n.intervals.min),
            (n.userRange ? n.userRange.max : n.intervals.max) );

        // format numbers used as tick marks, in the same way as makeNumericAxis()
        let decimalPrecision = axisOptions.subType ? undefined : Dyn.mathUtil.decimalPlaces( this.numeric.intervals.intervalWidth ),
			altFormatString = ( axisOptions.subType !== Dyn.dataUtil.subtypes.kFraction) ? undefined :
				Dyn.fractionData.getAxisFormat( formattingAttribute.format.formatString, formattingAttribute.format.axisFormat, n.intervals.intervalWidth );
		this.numeric.intervals.labels = this.numeric.intervals.values
			.map( v => formattingAttribute.valueToString( v, decimalPrecision, altFormatString ));
        // console.log("_updateNumericAxisOptions numeric axis userRange="+ this.numeric.userRange+" plotRange="+ this.numeric.plotRange, n.axisRange, axisOptions );
    }

	/**
	 * Set the axis thickness (axis size perpendicular to axis)
	 * @param numTitles	{number} - 0+ number of titles to reserve space for
	 * @param {number} newTitleMargin - 0+ pixels reserved for margin below/left of title
	 * @param newLabelThickness {number} - 0+ pixels reserved for labels
	 * @param newTickThickness {number} - 0+ pixels reserved for tick marks
	 */
	setAxisThickness( numTitles, newTitleMargin, newLabelThickness, newTickThickness ) {
		var titleThickness = ( numTitles * this.titleXHeight ), // covers title text and title buttons
			titlePlusTickThickness = newTitleMargin + titleThickness + newTickThickness,
            minThickness = Dyn.plotUtil.kMinAxisThickness,
			newAxisThickness =
					newTitleMargin +		// border space on left/bottom (includes space for extra attribute drop zone)
					titleThickness + 		// then 0+ title strings (attribute names or prompt or freq/perc/prop)
					newLabelThickness + 	// then 0+ space for labels, if any
					newTickThickness;		// then 0+ space for ticks, if any
        if( Dyn.plotUtil.isDrawOnly() ) {
            newAxisThickness = 0; // hide axis if in draw-only mode
        } else {
            // clip to minimum and maximum axis thickness
            newAxisThickness = Dyn.mathUtil.clipToMinMax( minThickness, this.maxAxisThickness, newAxisThickness );

            // adjust this.labelThickness to clip the label text to this length
            // NOTE: because we want the labels to be shortened, instead of the title or ticks, when we go over maximum thickness.
            this.labelThickness = newAxisThickness - titlePlusTickThickness;
            this.tickThickness = newTickThickness;
            this.titleMargin = newTitleMargin;
            Dyn.assert( Dyn.mathUtil.isClose( newAxisThickness, this.titleMargin + titleThickness + this.labelThickness + this.tickThickness ) );
        }

		// set as width or height of bounds, used by view layout later (updateVisuals())
		if( this.isHorizontal ) {
			this.bounds.y = this.bounds.bottom() - newAxisThickness;
			this.bounds.height = newAxisThickness;
		} else {
			this.bounds.width = newAxisThickness;
		}
	}

	/*
	 * Show/Hide the components of axis before getting
	 * image, or hide for view-only mode
	 * - cross sign next to attribute name
	 * - + and - button on histogram
	 * - numeric adjustment buttons on numeric axis
	 * - hide the axis if its not used, disabled
	 * - for numberlineaxis, hide the drag buttons
	 */
	setViewMode( mode ) {
		const showControls = mode.showControls(),
			showAxes = mode.showAxes(),
			wasHidingAxisControls = this.hideControls;

		this.hideControls = ! showControls; // update local setting

        // do we need to reposition titles to make room for controls? Or use space previously used by controls?
        if( wasHidingAxisControls !== this.hideControls ) {
            this.updateTitles();
        }

		if( this.isPromptAxis() ) {
            // hide the prompt axis, which has one title and no attributes
            Dyn.plotUtil.showOrHideElement( this.dropRect, showControls );
            this.titles.forEach( (title) => {
                Dyn.plotUtil.showOrHideElement( title.text, showControls );
                Dyn.plotUtil.showOrHideElement( title.closeBox, showControls );
            });
        } else {
			// hide the closebox next to each individual attribute label on axis
            this.titles.forEach( (title) => {
                Dyn.plotUtil.showOrHideElement( title.closeBox, showControls );
			});

			// if number line axis, hide the drag buttons
			if ( this.isNumericalScaleAxis() ){
                Dyn.plotUtil.showOrHideElement( this.dragMiddle, showControls );
                Dyn.plotUtil.showOrHideElement( this.dragMin, showControls );
                Dyn.plotUtil.showOrHideElement( this.dragMax, showControls );
            }
			if ( this.allowAnotherAttribute()) {
                Dyn.plotUtil.showOrHideElement( this.extraDropRect, showControls );
                Dyn.plotUtil.showOrHideElement( this.extraDropText, showControls );
			}

			// if histogram, hide the plus minus buttons
			if( this.isUsingPlusMinus ){
				Dyn.plotUtil.showOrHideElement( this.plusButton, showControls );
				Dyn.plotUtil.showOrHideElement( this.minusButton, showControls );
            }
            Dyn.plotUtil.showOrHideElement( this.settingsRect, showControls );
		}
        if(showControls) {
            Dyn.plotUtil.showOrHideElement( this.dropRect, showAxes );
            Dyn.plotUtil.showOrHideElement( this.extraDropRect, showAxes );
        }
	}

	/**
	 * re-layer the Rafael visual elements so the correct ones are on top (z-axis).
	 * designed to be compatible with Dyn.PlotView.layerVisuals
	 */
	layerVisuals( layerNumber ) {
        Dyn.assert(( layerNumber >= 0 && layerNumber <= 3 )); // layer 0 is the bottom layer
        switch( layerNumber ) {
            case 0:
                // minor grid lines for X and Y should be below all other grid lines
                this.minorLines.forEach((v) => v.toFront());
                break;
            case 1:
                // grid lines or ticks at tick points
                this.ticks.forEach( ( v ) => v.toFront() );
                break;
            case 2:
                // grid lines at origin for X and Y should be on top of all other grid lines
                this.originLine.forEach((v) => v.toFront());
                break;
            case 3:
                // 1.1 drop areas on bottom
                this.extraDropRect.toFront();
                this.extraDropText.toFront();
                this.dropRect.toFront();
                this.titleBounds.debugRect?.toFront();

                // 1.2: titles and bin lines on top of drop areas
                this.titles.forEach( ( value ) => {
                    value.text.toFront();
                    value.closeBox.toFront();
                    value.colorRect.toFront();
                    value.debugRect?.toFront();
                } );
                if( this.line ) this.line.toFront();
                this.binLines.forEach( ( v ) => v.toFront() );
                this.labelSelectRects.forEach( ( v ) => v.toFront() );
                this.labels.forEach( ( v ) => v.toFront() );

                // 1.3: buttons on top of lines
                this.minusButton.toFront(); // first after closebox in keyboard nav. order
                this.plusButton.toFront();
                this.settingsRect.toFront();
                this.dragMin.toFront();
                this.dragMiddle.toFront();
                this.dragMax.toFront(); // last in keyboard nav. order

                // layering complete
                this.needsRelayering = false;
                break;
        }
	}

	/** given a new axis bounding box, or new attribute update the visual elements to match. See also initVisuals() */
	updateVisuals( noAnimation=false) {
		let viewMode = new Dyn.PlotViewMode();

        this.updateTitleXHeight();
        this.updateDropZone();
        this.updateTitles(); // updates titles, and +/-/settings icons to match

        Dyn.axisUtil.updateSettingsButton( this.settingsRect );
        Dyn.axisUtil.updateAxisButton( this.plusButton );
        Dyn.axisUtil.updateAxisButton( this.minusButton );

        // update tooltips for current language
        this.plusButton.data( 'tooltip', Dyn.loc("increase number of bins"));
        this.minusButton.data( 'tooltip', Dyn.loc("decrease number of bins"));
        this.dragMiddle.data( 'tooltip', Dyn.loc("axis range"));
        this.dragMin.data( 'tooltip', Dyn.loc("axis minimum"));
        this.dragMax.data( 'tooltip', Dyn.loc("axis maximum"));
        this.settingsRect.data( 'tooltip', Dyn.loc("axis settings"));

        // remove old axis lines, tick marks, numeric/categoric labels (keep title/attribute name)
        this.removeLine();
        // Dyn.plotUtil.removeAndEmptyArrayOfElements( this.binLines );
        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.ticks );
        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.minorLines );
        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.originLine );
        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.labelSelectRects );
        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.labels );

		// create new axis lines, tick marks, numeric/categoric labels
		if( this.isHiddenLineLabelsTicks ) {
			this.makeNullAxis( noAnimation );
		} else if( this.isNumericalScaleAxis() ) {
			this.makeNumericAxis( noAnimation );
		} else if( this.isCategoricalActiveAxis() ) {
			this.makeCategoricalAxis( noAnimation );
		} else {
			this.makeNullAxis( noAnimation );
		}

        // set flag so caller knows that axes need re-layering (this.layerVisuals())
        this.needsRelayering = true;

		if( ! viewMode.showControls()) { // hide controls in view only mode
		    this.setViewMode( viewMode );
        }
	}

    /** update the title line height */
    updateTitleXHeight() {
        const titleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisTitleFontSize ),
            titleButtonHeight = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ),
            titleLineHeight = Dyn.plotUtil.getTextBBox( this.paper, "Xy", titleFontSize ).height;
        this.titleXHeight = Dyn.plotUtil.kAxisTitleMargin + Math.max( titleLineHeight, titleButtonHeight );
        return this.titleXHeight;
    }

    /**
	 * Update the drop zone boundaries.
	 * Should be called after this view's boundaries are updated.
	 */
	updateDropZone() {
		"use strict";

		// update the drop zone
		var enableExtraDropZone = this.allowAnotherAttribute(),
			extraWidth = ( enableExtraDropZone && !this.isHorizontal ) ? Dyn.plotUtil.extraDropZoneThickness : 0,
			extraHeight = ( enableExtraDropZone && this.isHorizontal ) ? Dyn.plotUtil.extraDropZoneThickness : 0,
			widthSpacer = ( enableExtraDropZone && !this.isHorizontal ) ? Dyn.plotUtil.extraDropZoneSpacer : 0,
			heightSpacer = ( enableExtraDropZone && this.isHorizontal ) ? Dyn.plotUtil.extraDropZoneSpacer : 0,
            xOverLapSpacer = this.isHorizontal ? 2 : 0, // shift visible drop zone on x-axis to left to show perfect alignment with Y in corners
            dropRect = new Dyn.mathUtil.Rect( this.bounds );
		if( this.marginForButtons ) {
            Dyn.assert( ! this.isHorizontal, "use marginForButtons for Y axis only");
            dropRect.y += this.marginForButtons;
            dropRect.height -= this.marginForButtons;
        }

		this.dropRect.attr( {
			x: dropRect.x + (( !this.isHorizontal ) ? widthSpacer + extraWidth : 0) - xOverLapSpacer,
			y: dropRect.y,
			width: dropRect.width - (widthSpacer + extraWidth) + xOverLapSpacer,
			height: dropRect.height - (heightSpacer + extraHeight) } );

        let narrower = Dyn.plotUtil.extraDropZoneWidth,
            extraRect = new Dyn.mathUtil.Rect(),
            yAxisX = ((this.axisID==='y2') ? this.bounds.width - extraWidth : 0);
		extraRect.x = this.bounds.x + ( this.isHorizontal ? (this.bounds.width * 0.5 *(1-narrower)) : yAxisX );
		extraRect.y = this.bounds.y + (( this.isHorizontal ) ? this.bounds.height - extraHeight : (this.bounds.height * 0.5 *(1-narrower)));
		extraRect.width = ( this.isHorizontal ? narrower * this.bounds.width : extraWidth );
		extraRect.height = ( this.isHorizontal  ? extraHeight : (narrower * this.bounds.height));

		this.extraDropRect.attr({ x: extraRect.x, y: extraRect.y, width: extraRect.width, height: extraRect.height });
		this.extraDropText.attr({ x: extraRect.centerX(), y: extraRect.centerY() });

		this.dropRect.attr({
			fill: this.getDropRectFillColor(),
			'fill-opacity': this.getDropRectFillOpacity(),
			'stroke-opacity': this.getDropRectStrokeOpacity()
		});
        this.dropRect.show();
        if( enableExtraDropZone ) {
            this.extraDropRect.attr( { fill: Dyn.plotUtil.promptDropFillColor } ).show();
            this.extraDropText.show();
        } else {
            this.extraDropRect.hide();
            this.extraDropText.hide();
        }
		//console.log( "updateDrapZone "+(this.isHorizontal?"X":"Y")+" fill"+this.dropRect.attr("fill"))
	}

	/**
	 * Update (add/remove/reposition) the bin lines, so that the given axis bins are visible.
	 *
	 * @param {Dyn.PlotShaper.bins} bins -- bins form by the axes
	 */
	updateBinLines( bins ) {
		"use strict";
		var binList = ( this.isHorizontal ? bins.xBins : bins.yBins ),
			oldNumLines = this.binLines.length,
			newNumLines = ( this.showBinLines ? Math.max(0, binList.length - 1 ) : 0 ),
			numLinesToRemove = Math.max(0, oldNumLines-newNumLines),
            yAxisWithCategoriesBottomToTop = this.categoriesFromBottomToTop();
        // Y-axis with categorical:numericIntervals has categories from bottom to top, not top to bottom like normal categories

		// remove unneeded lines
		for( let i=0; i<numLinesToRemove; i++ ) {
			this.binLines.pop().remove();
		}

		// add/update needed lines
		for( let i=0; i<newNumLines; i++ ) {
			let bin = binList[i];
			if ( i < this.binLines.length ) {
				// update line position
				this.binLines[i].attr({ path: makeLinePath( binList[i], this.isHorizontal, this.margin )});
			} else {
				// add a new line
				this.binLines.push(
					this.paper.path( makeLinePath( binList[i], this.isHorizontal, this.margin )).
					attr({ stroke: Dyn.plotUtil.kBinLineColor }));
			}
		}
		Dyn.assert( this.binLines.length === newNumLines );

		function makeLinePath( binRect, isHorizontalAxis, margin ) {
			var pixCross = (yAxisWithCategoriesBottomToTop ?
                            binRect.rightOrTop( isHorizontalAxis ) :
                            binRect.rightOrBottom( isHorizontalAxis )).toFixed(1 ),
				pixMin   = ( isHorizontalAxis ? binRect.bottom()-margin : binRect.left()+margin ),
				pixMax   = ( isHorizontalAxis ? binRect.top()+margin    : binRect.right()-margin );

			if( isHorizontalAxis ) {
				// if horizontal axis: vertical line at right of bin rectangle
				return "M" + pixCross + " " + pixMin + "L" + pixCross + " " + pixMax;
			} else {
				// if vertical axis : horizontal line at top of bin rectangle
				return "M" + pixMin + " " + pixCross + "L" + pixMax + " " + pixCross;
			}
		}
	}


	/**
	 * reposition and show or hide the axis title(s) each with close button and possibly a color icon.
	 * @returns {number} -- available width for title string
	 */
	updateTitles() {
        // adjust title text, closebox, etc.
        var strings               = (this.titleString ? this.titleString.split("\n") : []),
            numAttribOrPromptTitles = strings.length, // titles from setTitle()
            numAxisScaleTitles    = (this.isNonAttributeScaleAxis() ? 1 : 0), // additional title if a frequency axis
            numTitleLines         = Math.max( 1, numAttribOrPromptTitles + numAxisScaleTitles ),
            newThickness          = this.titleXHeight * numTitleLines,
            titleBounds           = this.titleBounds,
            // isNonEmptyTitleString = (this.titleString.length > 0),
            numActiveAttributes     = this.allAttributes.countActive(),
            isY2Axis                = (this.axisID==='y2'),
            isMultiAttribute        = this.allAttributes.countActiveIncludingDualAxis() > 1,
            numIconStylesToSkip     = isY2Axis ? (this.firstAxis.allAttributes.countActive()) : 0,
            titleToTickSpacer       = this.titleXHeight * 0.1;

        if( isY2Axis ) {
            isMultiAttribute = (this.firstAxis.allAttributes.countActiveIncludingDualAxis() > 1) && // two attributes on Y+Y2 axis
                (this.allAttributes.countActive() > 0); // and at least one attribute on Y2 axis
            // add a little space between Y2 title and ticks; X or Y axis have no space between ticks and title, this is usually OK
            //  because there is a blank space to make room for font descenders (e.g. bottom of lower case 'g' goes lower than 'e').
            titleToTickSpacer = this.titleXHeight * 0.15;
        }

        // add strings for Frequency/Percent/Proportion axes, before any disabled attribute titles
        if( this.isFrequencyAxis()) {
            strings = [Dyn.loc( AxisView._getSharedFrequencyAttribute().name )].concat(strings);
        } else if ( this.isPercentAxis()) {
            strings = [Dyn.loc( AxisView._getSharedPercentAttribute().name )].concat(strings);
        } else if ( this.isProportionAxis()) {
            strings = [Dyn.loc( AxisView._getSharedProportionAttribute().name )].concat(strings);
        }
        Dyn.assert( numTitleLines >= this.allAttributes.count() );

        // determine which buttons should be visible, which affects the space available for titles
        //  1. if this.marginForButtons > 0, reserve space for buttons from other sources, e.g. Map Plot button on Y axis
        //  2. if there are multiple titles, only attribute titles will have a close box (if not read only), not Freq/Percent/Proportion/Prompts
        //  3. if there are multiple attributes on the axis, there will be a color rect for each attribute
        //  3. 1st title may also have a settings, plus, and minus buttons
        let showAnyButtons = (numTitleLines>0) && ! this.hideControls,
            showCloseBox0 = showAnyButtons && ! this.isNonAttributeScaleAxis(),
            show = {
                anyColorRects: (numTitleLines>0) && isMultiAttribute, // not a button, just a color indicator
                anyCloseBoxes: showCloseBox0 && (numAttribOrPromptTitles > 0),
                plusButton: showCloseBox0 && this.isUsingPlusMinus,
                minusButton: showCloseBox0 && this.isUsingPlusMinus,
                settingsRect: showAnyButtons && ((this.allAttributes.countActive() > 0) || this.isNonAttributeScaleAxis()),
            },
            numButtonsBefore = [ show.anyColorRects ].reduce( ( a, c ) => a + c, 0 ),
            numButtonsAfter = [ show.anyCloseBoxes, show.plusButton, show.minusButton, show.settingsRect  ].reduce( ( a, c ) => a + c, 0 ),
            bboxOfCloseBox0 = null,
            buttonSpacing = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ) + Dyn.plotUtil.axisButtonSpacing;

        // determine bounds of first line of text (iterate later if 2+ titles for 2+ attributes)
        titleBounds.x      = (this.isHorizontal ? this.bounds.x :
            (isY2Axis ? this.bounds.right() - (this.titleMargin + newThickness) + titleToTickSpacer :
                        this.bounds.left()  + this.titleMargin + newThickness - this.titleXHeight - titleToTickSpacer));
        titleBounds.y      = (this.isHorizontal ? this.bounds.bottom() - (this.titleMargin + newThickness) + titleToTickSpacer : this.bounds.y);
        titleBounds.width  = Math.max(0, (this.isHorizontal ? this.bounds.width : this.titleXHeight));
        titleBounds.height = Math.max(0, (this.isHorizontal ? this.titleXHeight : this.bounds.height));
        if( this.marginForButtons ) {
            Dyn.assert( !this.isHorizontal, "use marginForButtons for Y axis only" );
            let offset = this.marginForButtons + 10; // we need this offset of 10 to align with drop zone, not sure why --CDM 2017-10-24
            titleBounds.y += offset;
            titleBounds.height -= offset;
        }
        if( strings ) {
            titleBounds.addDebugRect( this.paper, "green" );
        }

        // adjust title bounds if settings icon is enabled (settings icon will be hidden later if viewOnly)
        Dyn.assert( Dyn.preferences.isViewOnly() ? this.hideControls===true : true, 'hideControls not properly set for viewOnly mode');

        // adjust titleBounds to make room for buttons
        if( ! this.hideControls ) {
            let spaceBefore = numButtonsBefore * buttonSpacing,
                spaceAfter = numButtonsAfter * buttonSpacing;
            if( this.isHorizontal ) {
                titleBounds.x += spaceBefore;
                titleBounds.width = Math.max(0, titleBounds.width - spaceBefore - spaceAfter);
            } else {
                titleBounds.y += spaceAfter;
                titleBounds.height = Math.max( 0, titleBounds.height - spaceBefore - spaceAfter );
            }
        }

		// add or update titles
		for( let i=0, j=strings.length; i<j; i++ ) {
			// add titles
			let t = this.titles[i],
                str = strings[i] || "", // note: string may be "(drag and drop..." if this.isPromptAxis()
                wantMathJaxStr = hasMathJaxPattern(str),
                isPercFreqTitle = (i<numAxisScaleTitles),
                isTextDisabled = (i >= ( numAxisScaleTitles + numActiveAttributes)) && ( ! this.isPromptAxis()),
                isAlertText = this.isIncompleteAxis() /*&& ! isTextDisabled*/,
                isCloseVisible = showAnyButtons && ! isPercFreqTitle, // hide closebox for Frequency or Percent axis title which are not closable
                isColorRectVisible = isMultiAttribute && ! isPercFreqTitle,
                attributeIndex = i - numAxisScaleTitles, // only used if not isPercFreqTitle
                iconStyleIndex = attributeIndex + numIconStylesToSkip; // index into Dyn.plotUtil.kAttributeIconStyles

            // console.log("updateTitles " + (this.isHorizontal?"H":"V") + " x:"+titleBounds.centerX()+" y:"+titleBounds.centerY()
            //     +" margin"+this.titleMargin, " wantMathJaxStr:", wantMathJaxStr, "index", i, "|"+str+"|" );
			if( t ) { // remove then recreate the text (may be change to/from MathJax)
                t.text.remove(); // Remove the element and recreate it everytime
                Dyn.plotUtil.removeDebugRect( t );
			} else { // create the new text+closeBox+colorRect
				this.titles.push({});
				t = this.titles[i];
				t.closeBox = Dyn.axisUtil.makeCloseBox( this, attributeIndex );
                t.colorRect = Dyn.axisUtil.makeColorRect( this, attributeIndex );
            }
            t.text = this.paper.text( titleBounds.centerX(), titleBounds.centerY(), str );

            if( wantMathJaxStr ) {
                // TODO: move this section to a separate function for readability
                // var bbox = t.text.node.getBBox()
                t.text.remove(); // Remove the element and recreate it everytime
                t.text = this.paper.g();
                t.center = { x:titleBounds.centerX(), y:titleBounds.centerY() }; // center point for setSvgTitle

                const setSvgTitle = (_svg) => {
                    // The _ele.node is removed at some point before the mathjax conversion is over.
                    // So ignore applying converted svg into those null reference element node
                    // Example: Element node becomes null when a attribute is selected
                    // TODO: Figure out the place where _ele.node becomes null and try to avoid it if possible
                    if(!t.text.node) {
                        return;
                    }

                    // Cache created SVG for future use
                    this.svgCache = {
                        attrname: str,
                        svg: _svg
                    }
                    // Set style properties
                    _svg.style.fontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisTitleFontSize ) - 2;
                    _svg.style.fontFamily = Dyn.plotUtil.kAxisFontFamily;
                    _svg.style.fontWeight = "bold";
                    _svg.style.color = Dyn.plotUtil.titleColor;

                    t.text.node.appendChild(_svg);
                    let cx = t.center.x,
                        cy = t.center.y,
                        bbox = t.text.node.getBBox();
                    //console.log("setSvgTitle 1", cx, cy, titleBounds, bbox, str );
                    // To position it in center of the give x and y position
                    if(this.isHorizontal) {
                        t.text.attr({
                            // transform: 'r0T'+ Math.round(cx - (bbox.width/2)) +',' + Math.round(cy - (bbox.height/2)),
                            x: Math.round(cx - (bbox.width/2)),
                            y: Math.round(cy - (bbox.height/2)),
                            width: bbox.width,
                            height: bbox.height,
                            cx: cx,
                            cy: cy
                        });
                        // Setting tranform directly due to the bug with rapheal transform
                        t.text.node.setAttribute("transform", 'matrix(1,0,0,1,'+ Math.round(cx - (bbox.width/2)) +',' + Math.round(cy - (bbox.height/2)) + ')');
                    } else {
                        t.text.attr({
                            // transform: 'r-90T'+ Math.round(cx - (bbox.height/2)) +',' + Math.round(cy + (bbox.width/2)),
                            x: Math.round(cx + (bbox.height/2)),
                            y: Math.round(cy - (bbox.width/2)),
                            width: bbox.width,
                            height: bbox.height,
                            cx: cx,
                            cy: cy
                        });
                        // Setting tranform directly due to the bug with rapheal transform
                        t.text.node.setAttribute("transform", 'matrix(0,-1,1,0,'+ Math.round(cx - (bbox.height/2)) +',' + Math.round(cy + (bbox.width/2)) + ')');
                    }

                    t.bbox = Dyn.axisUtil.getBBoxForG( t.text );
                    // position individual close boxes for multiple attributes, and show/hide
                    Dyn.axisUtil.updateCloseBox( this, t.closeBox, isCloseVisible, t.bbox, attributeIndex );
                    Dyn.axisUtil.updateColorRect( this, t.colorRect, isColorRectVisible, t.bbox, attributeIndex, iconStyleIndex, this.viewRefs );
                }

                // If svg cache is available and the attribute name is same, use the cached svg
                if( this.svgCache && this.svgCache.attrname===str && this.svgCache.svg ) {
                    setSvgTitle(this.svgCache.svg);
                    Dyn.debugMathJax && console.log("AxisView.updateTitles: using cached svg '", str, "'");
                } else { // else create new SVG
                    Dyn.axisUtil.mathJaxTxtToSVG(str, setSvgTitle);
                    Dyn.debugMathJax && console.log("AxisView.updateTitles: mathJaxTxtToSVG, no cached svg '", str, "'" );
                }
            }

            // set styles and attributes for text, closebox
			let axisClass = isAlertText ? 'tde-attribute-name tde-alert ' + Dyn.plotUtil.hideOnPreviewClass :
                ('tde-attribute-name' + (this.isPromptAxis()? ' ' + Dyn.plotUtil.hideOnPreviewClass : '' ) + (isTextDisabled? ' tde-disabled': ''));
			t.closeBox.data( "tooltip", Dyn.loc("remove {{attributeName}}", { attributeName:str }));

			let ariaLabel;
			if( this.isHorizontal ) {
				ariaLabel = Dyn.stringFormat(Dyn.plotUtil.axisCloseButtonAriaLabel, str, 'X-Axis');
			} else {
				ariaLabel = Dyn.stringFormat(Dyn.plotUtil.axisCloseButtonAriaLabel, str, 'Y-Axis');
			}
            t.closeBox[0].node.setAttribute( "aria-label", ariaLabel );

            t.text.attr({
                    "font-size": Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisTitleFontSize ),
					'font-family': Dyn.plotUtil.kAxisFontFamily,
					fill: Dyn.plotUtil.titleColor,
					// fill:isAlertStyle ?'#c10000':(isTextDisabled? '#9e9e9e':'#000'),
                    class: axisClass
                })
			    .data( "tooltip", str ) // tooltip has full title
                .data( "isMathJax", wantMathJaxStr ) // used by enableDisableForDragDrop()
                .hover( this.hoverOverLabel, this.hoverOutLabel );
            if( wantMathJaxStr )
                Dyn.plotUtil.disableMouseOverEvents( t.text ); // see also enableDisableForDragDrop()

            // clip and ellide (...) the title if a non-MathJAX title
            if(t.text.type === 'g') {
                t.bbox = Dyn.axisUtil.getBBoxForG( t.text );
            } else {
                // clip the title text and get the bbox
    			t.bbox = Dyn.plotUtil.clipStringToWidth( t.text, titleBounds.widthOrHeight( this.isHorizontal));
            }
            Dyn.plotUtil.addDebugRect( this.paper, t, t.bbox, "#fa2d79" );

            // position individual close boxes for multiple attributes, and show/hide
            Dyn.axisUtil.updateCloseBox( this, t.closeBox, isCloseVisible, t.bbox, attributeIndex );
            Dyn.axisUtil.updateColorRect( this, t.colorRect, isColorRectVisible, t.bbox, attributeIndex, iconStyleIndex, this.viewRefs );
            if( i===0 ) {
                bboxOfCloseBox0 = t.closeBox.getBBox();
            }

			// advance to next attribute if any
			if ( this.isHorizontal ) {
				titleBounds.y += this.titleXHeight;
			} else {
				// rotate last to work around bug #21 (rotated text disappears randomly)
                if(t.text.type == 'g') {
                    // Don't do transformation for g
                } else {
                    t.text.attr( "transform", "r-90" );
                    t.debugRect?.attr( "transform", "r-90" );
                }
				titleBounds.x -= this.titleXHeight;
			}
		}

        this.updateTitleButtons( show, buttonSpacing, bboxOfCloseBox0 ); // position/hide/show controls next to title.

		// remove extra titles
		for( let i=this.titles.length, j=strings.length; i>j; i-- ) {
			let extraTitle=this.titles.pop();
			if( extraTitle.text )
				extraTitle.text.remove();
			if( extraTitle.closeBox )
				extraTitle.closeBox.remove();
			if( extraTitle.colorRect )
				extraTitle.colorRect.remove();
            Dyn.plotUtil.removeDebugRect( extraTitle );
		}
        Dyn.assertValidArray( this.titles );
	}

    /**
     * Reposition and show or hide 3 axis buttons: Settings/Gear, Plus, Minus.
     * See updateTitles() for repositioning the Close and Color button next to each axis title.
     * @param show {*} -- which buttons should be visible
     * @param spacing {number} -- distance from one button to the next in pixels
     * @param bboxOfCloseBox0 {*} -- position of the close button of the 1st attribute if any
     */
	updateTitleButtons( show, buttonSpacing, bboxOfCloseBox0 ) {

        // starting with position of the closebox as the first button (already placed, maybe hidden),
        // set up positions of buttons, including some micro-adjustments to align perfectly with closebox and title
        const closeBoxRect = new Dyn.mathUtil.Rect( bboxOfCloseBox0 ), // copy or create empty rect
            nextX = this.isHorizontal ? buttonSpacing : 0, // X-axis: next button to right
            nextY = this.isHorizontal ? 0 : - buttonSpacing,  // Y-axis: next button above
            xAdj = 2.5, // micro-adjust to align with closebox
            yAdj = 1.5; // micro-adjust to align with closebox
        let xPos = this.isHorizontal ? closeBoxRect.right() + buttonSpacing/2 + xAdj: closeBoxRect.centerX() + 0.5,
            yPos = this.isHorizontal ? closeBoxRect.centerY() + 0.5 : closeBoxRect.top() - (buttonSpacing/2 + yAdj);
        if( ! show.anyCloseBoxes ) { // shift back if no close box
            xPos -= nextX - (this.isHorizontal ? xAdj : 0);
            yPos -= nextY + (this.isHorizontal ? 0 : yAdj);
        }

        // position the 3 buttons above (in Y) or to the right (in X) of the close box
        showHideNextButton( show.minusButton, this.minusButton );
        showHideNextButton( show.plusButton, this.plusButton );
        showHideNextButton( show.settingsRect, this.settingsRect );

        function showHideNextButton( showHide, button ) {
            if( showHide ) {
                Dyn.assert( bboxOfCloseBox0, "Need a closebox position" );
                button.transform( "t" + xPos + "," + yPos ).toFront();
                button.show();
                xPos += nextX;
                yPos += nextY;
            } else {
                button.hide();
            }
        }
	}

	/**
	 * Update numeric axis dragging buttons:
	 *     position and show/hide according to the axis status
	 * @param {boolean} [wantAnimation=false]
	 */
	updateDragButtons( wantAnimation ) {
		// if axis is numeric and not a map axis, position and show drag buttons
		if( this.isNumericalScaleAxis() && ! this.isNonLinearMap ) {
			let pixWidth = this.bounds.widthOrHeight( this.isHorizontal ) - Math.max(0, this.margin+this.marginForSum ),
				pixMin   = this.bounds.leftOrBottom( this.isHorizontal ) + (this.kDragMinFract*pixWidth*(this.isHorizontal? 1 : -1 )),
				pixMax   = this.bounds.leftOrBottom( this.isHorizontal ) + (this.kDragMaxFract*pixWidth*(this.isHorizontal? 1 : -1 )),
                isY2Axis = (this.axisID==='y2'),
                dragButtonOffset = isY2Axis ? 0 : -this.kDragButtonHeight,
				pixCross = ( this.isHorizontal ? this.bounds.top() : (isY2Axis ? this.bounds.left() : this.bounds.right())),
				pixMiddle = (0.5 * (pixMax + pixMin - this.kDragButtonWidth)),
				xMiddle = ( this.isHorizontal ? pixMiddle : pixCross + dragButtonOffset ),
				yMiddle = ( this.isHorizontal ? pixCross : pixMiddle ),
				xMin = ( this.isHorizontal ? pixMin : pixCross + dragButtonOffset ),
				yMin = ( this.isHorizontal ? pixCross : pixMin - this.kDragButtonWidth ),
				xMax = ( this.isHorizontal ? pixMax - this.kDragButtonWidth : pixCross + dragButtonOffset ),
				yMax = ( this.isHorizontal ? pixCross : pixMax ),
				animationTime = ( wantAnimation ? Dyn.plotUtil.quickAnimationTimeMs : Dyn.plotUtil.noAnimationMs ),
				animationEasing = Dyn.plotUtil.quickAnimationEasing;

			if ( ! this.isInDrag ) { // position drag button unless in drag (where it repositions itself)
                if( ! this.allAttributes.isSortAscending ) {
                    // if descending axis, swap button positions to match axis
                    var temp;
                    temp=xMin; xMin=xMax; xMax=temp; // swap
                    temp=yMin; yMin=yMax; yMax=temp; // swap
                }
                // note: we save x and y for startDraggableNumberline(); transform actually is what moves the button because it is a path
				this.dragMiddle.animate( { /*x: xMiddle, y: yMiddle,*/ transform:"t"+xMiddle+","+yMiddle }, animationTime, animationEasing );
                this.dragMiddle.getSymbolElement().data({ x:xMiddle, y:yMiddle });
				this.dragMiddle.show();

				this.dragMin.animate( { /*x: xMin, y: yMin,*/ transform:"t"+xMin+","+yMin }, animationTime, animationEasing );
                this.dragMin.getSymbolElement().data({ x:xMin, y:yMin });
				this.dragMin.show();

				this.dragMax.animate( { /* x: xMax, y: yMax,*/ transform:"t"+xMax+","+yMax }, animationTime, animationEasing );
                this.dragMax.getSymbolElement().data({ x:xMax, y:yMax });
				this.dragMax.show();

                this._expandCoverRect( pixMin, pixMax );
			}
            let range = this.numeric.axisRange,
                attribute = this.getAttribute();
            this.dragMin.getCoverElement()?.node.setAttribute("aria-valuenow", attribute?.valueToString(range.min));
            this.dragMax.getCoverElement()?.node.setAttribute("aria-valuenow", attribute?.valueToString(range.max));
            this.dragMiddle.getCoverElement()?.node.setAttribute("aria-valuenow", attribute?.valueToString((range.max + range.min) / 2));
		} else {
			this.dragMiddle.hide();
			this.dragMin.hide();
			this.dragMax.hide();
		}
    }

    /**
     * Expand the cover rectangles for easier dragging of numeric axis drag buttons.
     * Like Fathom or Codap, we make the middle button cover 20% of the axis, and the min/max buttons cover 40% each.
     * @param pixMin
     * @param pixMax
     * @private
     */
    _expandCoverRect( pixMin, pixMax ) {
        const point4 = 0.4;
        const point6 = 0.6;
        const isSortAscending = Boolean( this.getFormattingAttribute()?.isSortAscending );
        const w = this.kDragButtonWidth;
        let minCoverEl = this.dragMin.getCoverElement(),
            middleCoverEl = this.dragMiddle.getCoverElement(),
            maxCoverEl = this.dragMax.getCoverElement();
        if( this.isHorizontal ) {
            let p0 = pixMin, // p0..p3 are positions along the axis in view coordinates from min to max (left to right if isSortAscending)
                p1 = (point4 * (pixMax + pixMin)),
                p2 = (point6 * (pixMax + pixMin)),
                p3 = pixMax,
                attrsMin = { x:-w, width: Math.max(1, w + p1 - p0) },
                attrsMax = { x: p2 - p3 + w, width: Math.max(1, w + p3 - p2) };
            minCoverEl.attr( isSortAscending ? attrsMin : attrsMax );
            middleCoverEl.attr( { x: -0.5*(p2-p1), width: Math.max(1, w + p2 - p1) } );
            maxCoverEl.attr( isSortAscending ? attrsMax : attrsMin );
        } else {
            let p0 = pixMax, // p0..p3 are positions along the axis in view coordinates from max to min (top to bottom if isSortAscending)
                p1 = (point4 * (pixMax + pixMin)),
                p2 = (point6 * (pixMax + pixMin)),
                p3 = pixMin,
                attrsMin = { y:-(p1 - p0 - w), height: Math.max(1, w + p1 - p0) },
                attrsMax = { y: -w, height: Math.max(1, w + p3 - p2) } ;
            minCoverEl.attr( isSortAscending ? attrsMin : attrsMax );
            middleCoverEl.attr( { y: -0.5*(p2-p1), height: Math.max(1, w + p2 - p1) } );
            maxCoverEl.attr( isSortAscending ? attrsMax : attrsMin );
        }
	}

    /** get the grid line colors for this axis based on theme and plot settings */
    _getAxisGridColors() {
        let themeGridStyle = Dyn.plotUtil.axisGridStyle,
            plotGridStyle = Dyn.plotView?.plotShaper.findElement( Dyn.EPlotControl.kControlsGridLines )?.getGridStyle(),
            gridStyle = themeGridStyle || plotGridStyle || '', // theme change overrides plot setting, overrides default
            axisGridColorStyle = 'axisGridColors'+ (( typeof gridStyle === 'string' ) ? gridStyle : '');
        Dyn.assert( typeof gridStyle === 'string', "gridStyle should be a string" );
        return Dyn.plotUtil[axisGridColorStyle]; // axisGridColors, axisGridColorsGray, etc.
    }

	// draw a numeric axis from the already-set numeric attribute
	makeNumericAxis( noAnimation=false ) {
		Dyn.assert( Dyn.plotView, "no plot, check isTuvaJrPlay()" );
        const axisGridColors = this._getAxisGridColors();
		let isY2Axis = (this.axisID==='y2'),
            pixMin   = setPixMin( this ), // view position of axis endpoint
			pixMax   = setPixMax( this ), // view position of axis endpoint
            pixMinMax = (new Dyn.mathUtil.Range( pixMin, pixMax )).normalize(),
			pixCross = Math.round( this.isHorizontal ? this.bounds.top() : (isY2Axis? this.bounds.left()+this.tickThickness : this.bounds.right()) ),
            pixCross2 = Math.round( this.isHorizontal ? this.viewRefs.crossAxisBounds.top() + this.viewRefs.crossAxisMargin
                    : this.viewRefs.crossAxisBounds.right() - this.viewRefs.crossAxisMargin ),
			labelOffset = (this.isHorizontal ? AxisView.kTickLength + this.kTickLabelSpacer :
                ( isY2Axis ? this.kTickLabelSpacer : -(AxisView.kTickLength + this.kTickLabelSpacer))),
			animationTime = (this.isInDrag||noAnimation) ? Dyn.plotUtil.noAnimationMs : Dyn.plotUtil.stdAnimationTimeMs;

		function setPixMin( axis /*, isMapAxis*/) {
            // find view position of axis lower-left, excluding margins needed for buttons, etc.
            var pixMin   = ( axis.isHorizontal ? axis.bounds.left() : axis.bounds.bottom() );
            pixMin += ( axis.isHorizontal ? axis.margin : - axis.margin );
            return pixMin;
        }
        function setPixMax( axis /*, isMapAxis */) {
            // find view position of axis upper-right, excluding margins needed for buttons, etc.
            var pixMax   = ( axis.isHorizontal ? axis.bounds.right() : axis.bounds.top() );
            pixMax += ( axis.isHorizontal ? -(axis.margin+axis.marginForSum) : axis.margin+axis.marginForSum );
            return pixMax;
        }
        function makeTickLine( axis, pixCross, pixCross2, pixVal, tickLength, isOrigin, isMinor ) {
            // create a path for a single tick mark line or grid line at the given view position, perpendicular to the axis
            let lineColor = Dyn.plotUtil.kAxisTickColor,
				strokeWidth = 1,
				dashArray = null;
            if( axis.showGridLines ) {
                const majorMinor = isOrigin ? 0 : (isMinor ? 2 : 1);
                lineColor = axisGridColors[ majorMinor ];
				dashArray = Dyn.plotUtil.axisGridDashArray[ majorMinor ];
				strokeWidth = Dyn.plotUtil.axisGridStrokeWidth[ majorMinor ];
            }
            if( isMinor && ! axis.showGridLines ) {
                pixCross = (axis.isHorizontal ? pixCross + 1 : pixCross - 1 ); // offset short tick marks
            }
            let tickLine = (axis.isHorizontal ?
                // use paper.path(Raphael.format("M{0},{1}v{2}", x, y, -width));
                axis.paper.path( "M" + pixVal + "," + pixCross + (axis.showGridLines ? ("V"+pixCross2) : ("v"+tickLength))):
                axis.paper.path( "M" + pixCross + "," + pixVal + (axis.showGridLines ? ("H"+pixCross2) : ("h-"+tickLength))));
            tickLine
                .attr( { "stroke-opacity":0, "stroke": lineColor, 'stroke-width':strokeWidth } )
                .animate( { "stroke-opacity": 1 }, animationTime );
			tickLine.node.setAttribute( 'stroke-dasharray', dashArray );
            return tickLine;
        }

        // map plot axis range to view, swaping orientation if sort descending
        Dyn.assert( this.numeric && this.numeric.axisRange );
		if( this.allAttributes.isSortAscending ) {
			this.coordMap = new Dyn.mathUtil.CoordMap( pixMin, pixMax, this.numeric.axisRange.min, this.numeric.axisRange.max );
		} else {
			this.coordMap = new Dyn.mathUtil.CoordMap( pixMin, pixMax, this.numeric.axisRange.max, this.numeric.axisRange.min );
		}

        this.updateDragButtons(); // position and show numeric drag buttons

        if( this.isNonLinearMap )
            return; // on Map: hide line, ticks, labels, and axis drag buttons

        let lineMin  = this.viewRefs.crossAxisIsNumericalScaleAxis ? // extend numeric axis line to cross axis if numerical
            (this.isHorizontal ? this.viewRefs.crossAxisBounds.right()-this.kLineOffset : this.viewRefs.crossAxisBounds.top()+this.kLineOffset)
            : pixMin; // otherwise just to end of this axis
		this.line = // position axis line
			(this.isHorizontal ? this.paper.line( lineMin, pixCross+this.kLineOffset, pixMax, pixCross+this.kLineOffset )
							   : this.paper.line( pixCross-this.kLineOffset, lineMin, pixCross-this.kLineOffset, pixMax ) )
			.attr( { "stroke-opacity":0, "stroke": this.showGridLines ? "none" : Dyn.plotUtil.kAxisLineColor } )
			.animate( { "stroke-opacity": 1 }, animationTime );

		// make tick marks and labels
        let labelFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisLabelFontSize ),
            majorMinor = Dyn.dataUtil.calcMinorTickData( this.coordMap, this.numeric.intervals.niceWidthOptions,
                this.numeric.intervals.intervalWidth, this.numeric.intervals.isExactWidth );
        this.numeric.majorMinor = majorMinor; // save for getNumericAxisSteps()
		for( let i=-1, j=this.numeric.intervals.values.length; i<j; ++i ) {
		    // start at -1 to cover minor tick marks before first tick, major tick marks+labels are from 0 to j-1
            let dataVal = ( i === -1 ) ?
                ( this.numeric.intervals.values[0] - this.numeric.intervals.intervalWidth )
                : this.numeric.intervals.values[i];
            if( majorMinor.hasMinor ) {
                for( let m = 1; m < majorMinor.howMany; m++ ) { // ignore 0th tickmark which overlaps major
                    let minorPixVal = Math.round( this.coordMap.transform( dataVal + ( m * majorMinor.minorIntervalWidth )));
                    if( pixMinMax.isInRange( minorPixVal ) ) {
                        this.minorLines.push( makeTickLine( this, pixCross, pixCross2, minorPixVal, this.kMinorTickLength, false, true ) );
                    }
                }
                this.numeric.snapIntervalWidth = majorMinor.minorIntervalWidth;
            } else {
                this.numeric.snapIntervalWidth = majorMinor.majorIntervalWidth;
            }
            if( i >= 0 ) {
                let pixVal  = Math.round(this.coordMap.transform( dataVal )),
					label   = this.numeric.intervals.labels[i], // formattingAttribute.valueToString( dataVal, decimalPrecision );
					isOrigin = ( dataVal === 0 ),
                    tick = makeTickLine( this, pixCross, pixCross2, pixVal, AxisView.kTickLength, isOrigin, false ),
					textElem;
                (isOrigin ? this.originLine : this.ticks ).push( tick ); // push tick mark or grid line, separate origin line for layering
                this.labels.push(
                    textElem = ( this.isHorizontal ?
                        this.paper.text( pixVal, pixCross+labelOffset, label ) :
                        this.paper.text( pixCross+labelOffset, pixVal, label ) )
                        .attr( { "font-size": labelFontSize, class: 'tde-category-name', fill: Dyn.plotUtil.kAxisTickMarkTextColor } )
                );
                textElem.data( "tooltip", label );
                textElem.hover( this.hoverOverLabel, this.hoverOutLabel );
                if( this.isHorizontal === this.labelInfo.wantRotation ) {
                    Dyn.plotUtil.clipStringToWidth( textElem, this.labelThickness );
                }
                textElem.attr( "transform", this.getLabelTextTransform( textElem ) );
                Dyn.plotUtil.addTextElementDebugRect( this.paper, textElem, "green" );
            }
		}
		//console.log("makeNumericAxis()", this.isHorizontal ? "X-axis":"Y-axis" );
	}

	makeCategoricalAxis( noAnimation=false ) {
		var attribute = this.getAttribute(), // use first attribute only, ignore others if any
			numCategories = attribute.stats.numCategories(),
            catMargin = Dyn.plotUtil.extraMarginForCaseOutlines,
			pixMin   = ( this.isHorizontal ? this.bounds.left()+catMargin : this.bounds.top()+catMargin ),
			pixMax   = ( this.isHorizontal ? this.bounds.right()-catMargin : this.bounds.bottom()-catMargin ),
			pixCross = ( this.isHorizontal ? this.bounds.top() : this.bounds.right() ),
			labelOffset = AxisView.kTickLength + this.kTickLabelSpacer,
            //labelsArePerpendicularToAxis = (this.isHorizontal === this.labelInfo.wantRotation),
			minLabelSpacing = this.labelInfo.noRotateBBox.height; // minimum space for labels is text height

		// show bin lines when they at least minLabelSpacing apart (along with hiding labels below)
		const approxBinWidth = Math.abs( pixMax - pixMin ) * Dyn.mathUtil.invertSafely( numCategories );
		this.showBinLines = (approxBinWidth >= minLabelSpacing );

		// map locations of categories 0-(numcategories-1) to view positions (pixels)
        //  so that coordMap.transform( category n) puts category at center of the category's bin
        if( attribute.getDataType(true)==="categorical:numericIntervals" && ! this.isHorizontal ) {
            // Y-axis with categorical:numericIntervals has categories from bottom to top, not top to bottom like normal categories
            this.coordMap = new Dyn.mathUtil.CoordMap( pixMin, pixMax, numCategories - 0.5, -0.5 );
            Dyn.assert( (numCategories===0) || this.categoriesFromBottomToTop() );
        } else {  // else map category indices 0-(n-1) to view-left to view-right (in X) or view-top to view-bottom (in Y)
            this.coordMap = new Dyn.mathUtil.CoordMap( pixMin, pixMax, -0.5, numCategories - 0.5 );
            Dyn.assert( (numCategories===0) || ! this.categoriesFromBottomToTop() );
        }
		this.updateDragButtons(); // hide drag buttons
        let dragInfo = new Dyn.DragCategoryUtil( this,
            this.coordMap.transform( 0 ), this.coordMap.signedLength( 1 ), numCategories, attribute );

        function setHoverAndClick( axisView, element, attributeID, categoryName, categoryIndex ) {
            element.data( "attributeID", attributeID );
            element.data( "category", categoryName );
            element.data( "tooltip", categoryName );
            element.data( 'catIndex', categoryIndex );
            element.data( 'dragInfo', dragInfo );
            element.hover( axisView.hoverOverLabel, axisView.hoverOutLabel );
            //element.click( axisView.clickCategory );
            element.drag( axisView.dragMoveCategory, axisView.startDragCategory, axisView.endDragCategory );
        }

        // make tick marks and labels
        //  NOTE: if labels will overlap, we carefully hide every X number of labels, so that they don't.
        this.labelInfo.showEveryXLabelsStart( this.coordMap.scale );
        let labelFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisLabelFontSize );
		    // oldVal = -999;
        const radius = Dyn.plotUtil.axisTickMarkRadius;
		for( let i=0; i<numCategories; ++i ){
			let catString = attribute.valueToString( attribute.stats.categories[i]),
                isSelected = Dyn.dataSet.isCategorySelected( catString ),
                pixVal = this.coordMap.transform( i ),
                tickMark = (this.isHorizontal ?
					this.paper.circle( pixVal, pixCross + this.kLineOffset, radius ) :
					this.paper.circle( pixCross - this.kLineOffset, pixVal, radius ))
                    .attr( "fill", Dyn.plotUtil.kAxisLineColor );
            setHoverAndClick( this, tickMark, attribute.id, catString, i );
            this.ticks.push( tickMark );
            // hide the next N labels until so that labels are at least minLabelSpacing apart
			if( this.labelInfo.isVisibleLabel(i) ) {
				let textElem = (this.isHorizontal ?
						this.paper.text( pixVal, pixCross + labelOffset, catString ) :
						this.paper.text( pixCross - labelOffset, pixVal, catString ) )
                    .attr({
                        'font-size':labelFontSize,
                        fill: Dyn.plotUtil[ isSelected ? 'titleColorInverse' : 'titleColor' ],
                        class: 'tde-category-name'
                    });
				setHoverAndClick( this, textElem, attribute.id, catString, i );
				if ( this.labelInfo.labelsArePerpendicularToAxis ) {
				    // clip text length if very long and perpendicular to axis
					Dyn.plotUtil.clipStringToWidth( textElem, this.labelThickness );
				}
				textElem.attr( "transform", this.getLabelTextTransform( textElem ) );
				// oldVal = pixVal;
                if( isSelected ) {
                    // put a reverse-contrast selection rectangle behind selected label
                    let sr = new Dyn.mathUtil.Rect( textElem.getBBox() ).expand( 3, 3 ),
                        rectEl = this.paper.rect( sr.x, sr.y, sr.width, sr.height )
                            .attr({ fill: Dyn.plotUtil.titleSelectedColor })
                            .data( 'catIndex', i )
                            .data( 'rectOffset', this.isHorizontal
                                ? sr.x - textElem.attrs.x
                                : sr.y - textElem.attrs.y );
                    this.labelSelectRects.push( rectEl );
                    textElem.toFront(); // bring text to front of background
                }
                this.labels.push( textElem );
			}
		}
	}

	makeNullAxis( noAnimation=false ) {
		this.coordMap = makeCoordMap( this );

		if( Dyn.plotUtil.isDrawOnly() && this.showGridLines) {
			let crossAxis =( this.isHorizontal ? Dyn.plotView.yAxis : Dyn.plotView.xAxis ),
				crossCoordMap = makeCoordMap( crossAxis );
			makeMakeGridLines1D( this, crossCoordMap );
		}

		this.updateDragButtons(); // hide drag buttons

		/** make the coordinate map for the null axis, to create a normalized [0-1] coordinate system,
		 *  to position reference lines, movable lines, annotations, etc. on a non-numeric axis. */
		function makeCoordMap( axis ) {
			let nullMargin = axis.margin,
				pixMin   = ( axis.isHorizontal ? axis.bounds.left()+nullMargin : axis.bounds.bottom()-nullMargin ),
				pixMax   = ( axis.isHorizontal ? axis.bounds.right()-nullMargin : axis.bounds.top()+nullMargin );
			return new Dyn.mathUtil.CoordMap( pixMin, pixMax, 0, 1 );
		}

		/** make lines on one dimension (H or V) for draw-only mode on this axis */
		function makeMakeGridLines1D( axis, crossCoordMap ) {
			const pixCross = crossCoordMap.transform( 0.0 ),
				pixCross2 = crossCoordMap.transform( 1.0 ),
				numMinor = 40,
				origin = 20,
				majorEvery = 4,
				dataWidth = 0.025;

			for( let i=0; i<=numMinor; i++ ) {
				let pixVal = axis.coordMap.transform( i/numMinor ),
					gridIndex = (i===origin) ? 0 /*origin*/ : (((i % majorEvery) === 0) ? 1 /*major*/ : 2 /*minor*/),
					gridLine = axis.paper.path( axis.isHorizontal
							? ( "M" + pixVal + "," + pixCross + ("V"+pixCross2))
							: ( "M" + pixCross + "," + pixVal + ("H"+pixCross2)))
						.attr( { "stroke": Dyn.plotUtil.axisGridColors[ gridIndex ],
                            'stroke-width': Dyn.plotUtil.axisGridStrokeWidth[ gridIndex ] } );
				if( gridIndex !== 0 ) {
					gridLine.node.setAttribute( 'stroke-dasharray', Dyn.plotUtil.axisGridDashArray[ gridIndex ]);
				}
				axis.minorLines.push( gridLine );
			}
			axis.numeric.snapIntervalWidth = dataWidth;
		}
	}

	targetNumTicks() {
		// get the approximate number of ticks to create on an axis
		let width = ( this.isHorizontal ? this.bounds.width : this.bounds.height );
        const pixelsPerTick = 100;
        const minNumTicks = 2;
		let num = Math.max( minNumTicks, Math.ceil( width / pixelsPerTick )); // function of axis view size
		if( this.allAttributes.subType == Dyn.dataUtil.subtypes.kFraction ) {
			num = Dyn.fractionData.approxNumTicksOnAxis;
		}
		return num;
	}

	/** transform the data value (returned by getCaseDataAsNumber()) to a pixel coordinate in the axis dimension (x or y) */
	dataToPixel( dataValue, optionalAttributeIndex ) {
		"use strict";
		let coordMap = (this.secondAxis && (optionalAttributeIndex >= this.allAttributes.countActive())) ?
                this.secondAxis.coordMap :
                this.coordMap;
		return coordMap.transform( dataValue );
	}

    /**
     * Transform a plot view position (coordinate in Raphael paper/SVG) to a data coordinate.
     * This is the inverse of dataToPixel()
     */
    pixelToData( pixelValue, optionalAttributeIndex  ) {
        "use strict";
        let coordMap = (this.secondAxis && (optionalAttributeIndex >= this.allAttributes.countActive())) ?
                this.secondAxis.coordMap :
                this.coordMap;
        return coordMap.inverseTransform( pixelValue );
    }

	/**
	 * Get the case index as a real or coded numeric value usable by dataToPixel().
	 * @param caseIndex -- case index
	 * @param optionalAttributeIndex -- index of attributes on axis, assuming are 0+ attributes.
	 * @param {boolean} [preFilterOption] -- if true, compare to pre-filter categories.
	 * @returns {Number} -- numeric value or category index or zero.
	 */
	getCaseDataAsNumber( caseIndex, optionalAttributeIndex, preFilterOption ) {
		"use strict";
		var value,
			attribute = this.getActiveAttribute( optionalAttributeIndex || 0 ),
			stats;

		if ( this.isCategoricalAxis() ) {
			// categorical axis: get the category index, if not in any category then mark as missing.
			stats =( preFilterOption ? attribute.statsPreFilter : attribute.stats );
			value = attribute.cases[ caseIndex ];
			value = stats.categories.indexOf( value ); // convert to category index
			if ( value < 0 ) {
				value = Number.NaN; // if not in categories mark as missing
			}
		} else if ( this.isNumericalActiveAxis() ) {
			// numeric axis expects finite number (other values will convert to missing)
			value = attribute.cases[ caseIndex ];
			if( Dyn.dataUtil.getDataType( value )!=="number" ) {
				value = Number.NaN; // if not finite numeric, mark as missing
			}
		} else {
			// null axis or disabled numeric axis: just place within [0-1] range, no such thing as a missing value
			value = 0;
		}
		return value;
	}

    /**
     * Is the given point in any of our droppable areas?
     * @param xPosition
     * @param yPosition
     * @returns {{inDropRect: *, inExtraDropRect: *}}
     */
	isPointInDropRect( xPosition, yPosition ) {
		//console.log( "x :" + xPosition + " , " + "y: " + yPosition );

        function isInElementBBox( element ) {
            var bbox = element._getBBox(),
                rect = (new Dyn.mathUtil.Rect( bbox )).offset( offsetX, offsetY );
            Dyn.assertValidRect( rect, "isPointInDropRect invalid" );
            return( rect.isPointInRect( xPosition, yPosition ));
        }

		var bodyRect = document.body.getBoundingClientRect(),
			elemRect = this.paper.canvas.getBoundingClientRect(),
			offsetY = elemRect.top - bodyRect.top,
			offsetX = elemRect.left - bodyRect.left,
            inDropRect1 = isInElementBBox( this.dropRect ),
            inExtraDropRect = isInElementBBox( this.extraDropRect );

		return {
            inDropRect: inDropRect1 || inExtraDropRect,
            inExtraDropRect: inExtraDropRect
		};
	}

	/**
	 * Get the text transformation for labels rotated or not, on horizontal or vertical axis.
	 * Note: we need this because { 'text-anchor': 'start', 'transform': 'r90' } does not work as desired.
	 * @param textElem
	 * @returns {*}
	 */
	getLabelTextTransform( textElem ) {
		var t;
		if( this.isHorizontal ) {
			if( this.labelInfo.wantRotation ) {
                // X-axis vertical text: anchor to center-top, rotate 90 degrees clockwise
                t = Dyn.plotUtil.getTextTransform( textElem, "r90,center,top" );
			} else {
                // X-axis horizontal text: anchor to center-top
                t = Dyn.plotUtil.getTextTransform( textElem, "center,top" );
            }
		} else {
			if( this.labelInfo.wantRotation ) {
                // Y-axis vertical text: anchor to right-center, rotate 90 degrees counterclockwise
                t = Dyn.plotUtil.getTextTransform( textElem, "r-90,right,center" );
			} else {
                // Y-axis horizontal text: anchor to right-center
                t = Dyn.plotUtil.getTextTransform( textElem, "right,center" );
			}
		}
		return t;
	}

	/**
	 * update ioCurrentBounds to the maximum height and width of both bounding boxes.
	 * @param {{height:number,width:number}} ioCurrentBounds
	 * @param {{height:number,width:number}} iNewBounds
	 */
	//getMaxHeightWidth( ioCurrentBounds, iNewBounds ) {
	//	"use strict";
	//	if( ioCurrentBounds.height < iNewBounds.height )
	//		ioCurrentBounds.height = iNewBounds.height;
	//	if( ioCurrentBounds.width < iNewBounds.width )
	//		ioCurrentBounds.width = iNewBounds.width;
	//},

	/** how many bins are on this axis, created by categorical or numeric separation? */
	getNumSeparations() {
		"use strict";
		var hasCategories = this.isCategoricalActiveAxis(),
			attribute = this.getAttribute(),
			numCategories = attribute && attribute.stats.numCategories();
		return(( hasCategories && numCategories > 0 ) ? numCategories : 1 );
	}

    getNumAttributes() {
	    return this.allAttributes.attributes.length;
    }

	/**
	 * Get the attribute on the axis, or null if none.
     * Attribute may be inactive (disabled), see also getActiveAttribute(), getFormattingAttribute()
	 * @param {number} [optionalIndex=0] -- Can use optional 0+ index if multiple attributes on the axis
	 * @returns {Dyn.Attribute|null}
	 */
	getAttribute( optionalIndex ) {
        "use strict";
        var attribute = this.allAttributes.attributes[ optionalIndex || 0 ];
        return attribute || null;
    }

    /**
     * Get the attribute on the axis (or dual axis) if active, otherwise return null
     * @param index
     * @returns {*}
     */
    getActiveAttribute( attributeIndex ) {
	    return this.allAttributes.getActiveAttribute( attributeIndex );
    }

    /**
     * Get the axis view matching the attribute index, either this axis or its secondary axis.
     * To be used with getActiveOrDualAttributeCount() and getActiveAttribute()
     * to link this axis with a second (Dual) axis.
     * @param attributeIndex
     * @returns {Dyn.AxisView}
     */
    getSubviewForActiveAttribute( attributeIndex ) {
        return (this.secondAxis && (attributeIndex >= this.allAttributes.countActive())) ?
            this.secondAxis :
            this;
    }

    getSubviewIndexForActiveAttribute( attributeIndex ) {
        let activeInFirstAxis = this.allAttributes.countActive();
        return (this.secondAxis && (attributeIndex >= activeInFirstAxis )) ?
            attributeIndex - activeInFirstAxis : // index in second axis (if this is first)
            attributeIndex; // index in first axis (this axis)
    }

    /**
     * Get the attribute used to format our numberline.
     * For frequency axes, all the normal attributes should be disabled,
     * and a "Frequency" scale/attribute is used instead.
     */
    getFormattingAttribute( optionalIndex ) {
        let attribute = this.allAttributes.attributes[ optionalIndex || 0 ];
        if( this.isFrequencyAxis()) {
            Dyn.assert( this.allAttributes.countActive()===0 );
            attribute = AxisView._getSharedFrequencyAttribute();
        } else if( this.isPercentAxis()){
            // use percent attribute instead of numeric attributes if any
            attribute = AxisView._getSharedPercentAttribute();
        } else if( this.isProportionAxis()) {
            // use percent attribute instead of numeric attributes if any
            attribute = AxisView._getSharedProportionAttribute();
        }
        return attribute || null;
    }

        // private static variables, access via AxisView.#shared...
        static #sharedFrequencyAttribute = null; // shared static attribute, read-only, for getFormattingAttribute()
        static #sharedPercentAttribute = null; // shared static attribute, read-only, for getFormattingAttribute(
        static #sharedProportionAttribute = null; // shared static attribute, read-only, for getFormattingAttribute()

    /**
     * Get the shared static read-only attribute that is used to format a frequency axis.
     * For getting values on a frequency axis, which has no active attributes, we create a dummy attribute.
     * @return {Dyn.attribute}
     */
    static _getSharedFrequencyAttribute() {
        if( ! this.#sharedFrequencyAttribute ) {
            // create shared static frequency attribute
            let cases = [0, 1], // in range [0,1] for formatting the axis as integers,
                name = 'Frequency',  // can be blank if we don't want a name on the axis or tool tips
                id = '_Frequency_Axis_Attribute_'; // valid ID for coordinate tracking (Dyn.axisUtil.isNewAttributeCoord())
            this.#sharedFrequencyAttribute = new Dyn.Attribute( name, cases, 0, 1, id, null );
            // NOTE: we may want to override some Attribute set functions to make sure that it is read-only
        }
        return this.#sharedFrequencyAttribute;
    }
    static _getSharedPercentAttribute() {
        if( ! this.#sharedPercentAttribute ) {
            // create shared static frequency attribute
            let cases = ['0%', '1%'], // in range [0,1] for formatting the axis as integers with % formatting,
                name = 'Percent',  // can be blank if we don't want a name on the axis or tool tips
                id = '_Percent_Axis_Attribute_'; // valid ID for coordinate tracking (Dyn.axisUtil.isNewAttributeCoord())
            this.#sharedPercentAttribute = new Dyn.Attribute( name, cases, 0, 1, id, null );
            // NOTE: we may want to override some Attribute set functions to make sure that it is read-only
            // e.g. by creating class Dyn.AttributeReadOnly extends Dyn.Attribute in Attribute.js
        }
        return this.#sharedPercentAttribute;
    }
    static _getSharedProportionAttribute() {
        if( ! this.#sharedProportionAttribute ) {
            // create shared static frequency attribute
            let cases = [0, 0.001], // in range [0-0.001] for formatting the axis as 3-digit decimals,
                name = 'Proportion',  // can be blank if we don't want a name on the axis or tool tips
                id = '_Proportion_Axis_Attribute_'; // valid ID for coordinate tracking (Dyn.axisUtil.isNewAttributeCoord())
            this.#sharedProportionAttribute = new Dyn.Attribute( name, cases, 0, 1, id, null );
            // NOTE: we may want to override some Attribute set functions to make sure that it is read-only
        }
        return this.#sharedProportionAttribute;
    }

	/**
	 * Update the boundary rectangle to match the location and width of the separation.
	 * should only be called when there is a categorical axis with 2+ separations. (see getNumSeparations())
	 * @param binNum {number} - which bin, 0+
	 * @param ioRect {Dyn.mathUtil.Rect} - rectangle to be updated in x or y.
	 */
	updateRectToSeparation1D( binNum, ioRect ) {
		"use strict";
		Dyn.assert( this.isCategoricalAxis());
		/* makeCategoricalAxis() sets the coordMap as follows:
			range is (-0.5) to (number of bins - 0.5), so each integer 0+ is center of bin
			scale maps each unit to the number of pixels in each bin
		 */
		if( this.isHorizontal ) {
			ioRect.x = this.coordMap.transform( binNum - 0.5 );
			ioRect.width = this.coordMap.scaleAbs( 1 );
		} else {
            let offset = this.categoriesFromBottomToTop() ? 0.5 : -0.5;
			ioRect.y = this.coordMap.transform( binNum + offset );
			ioRect.height = this.coordMap.scaleAbs( 1 );
		}
	}

    /**
     * If we are on a Y axis, with categories 0-n listed from bottom to top of the view? If so return true.
     * Only Y axis with categorical:numericIntervals do we set up the coordinate transform for categories from bottom to top.
     * Note that with attributes categories in reverse sort, the 0th category position does not change (0th is the Z not A)
     * @return {boolean}
     */
    categoriesFromBottomToTop() {
        return( (!this.isHorizontal) && this.coordMap.transform( 0 ) > this.coordMap.transform(1) );
    }

	/**
	 * Get the desired minimum and maximum values of the axis if numeric (in attribute coordinates)
     * Note: this.numeric.axisRange is the final range after data, user, plot, map adjustments.
	 * @returns {Dyn.mathUtil.Range}
	 */
	getDesiredMinMax() {
		if( this.numeric ) {
		    var n = this.numeric;
		    if( n.userRange ) {
                return new Dyn.mathUtil.Range( n.userRange.min, n.userRange.max );
            } else if ( n.plotRange ) {
                return new Dyn.mathUtil.Range( n.plotRange.min, n.plotRange.max );
            } else if ( n.dataRange ) {
                return new Dyn.mathUtil.Range( n.dataRange.min, n.dataRange.max );
            } else {
                return new Dyn.mathUtil.Range( this.allAttributes.stats.min, this.allAttributes.stats.max );
            }
		} else {
            return new Dyn.mathUtil.Range( Math.NaN, Math.NaN );
        }
	}

    /**
     * Get the numeric range of the axis, or a NaN range if not numeric or the range has not yet been calculated.
     * See also getDesiredMinMax() which gets the data range which often is larger or smaller than the axis range.
     * e.g. data goes from 2-9.3 but axis goes from 0-10.
     * @returns {Dyn.mathUtil.Range}
     */
    getNumericAxisRange() {
	    let hasNumericAxisRange = this.numeric?.axisRange;
        if( hasNumericAxisRange ) {
            let n = this.numeric;
            return new Dyn.mathUtil.Range( n.axisRange.min, n.axisRange.max );
        } else {
            return new Dyn.mathUtil.Range( Math.NaN, Math.NaN );
        }
    }

	/**
	 * Set the axis range to include some new User-specified minimum and maximum.
	 * See also setPlotSpecificMinMax(), dropAttribute() which clears custom ranges.
	 * @param iRange  {Dyn.mathUtil.Range|null} -- valid range numbers or null to clear the custom range
     * @returns {boolean} -- true if user range has changed.
     */
	setUserCustomizedMinMax( iRange ) {
		let changed = false;
		Dyn.assert( this.numeric, "not a numeric axis" );
		if( this.numeric ) {
			changed = ! Dyn.mathUtil.equalRanges( this.numeric.userRange, iRange );
			if( iRange ) {
			    Dyn.assert( isFinite( iRange.min) && isFinite( iRange.max ));
				this.numeric.userRange = new Dyn.mathUtil.Range( iRange.min, iRange.max );
				this.numeric.userRange.normalize();
			} else {
				delete this.numeric.userRange;
			}
			if( changed )
				this.updateAxisTypeAndThickness(); // adjust this.numeric.axisRange and all dependent parameters
		}
		return changed;
	}

	/**
	 * Set the axis range to include some new Plot-type-specified minimum and maximum,
	 * See also setUserMinMax(), dropAttribute() which clears custom ranges.
	 * @param iRange {Dyn.mathUtil.Range|null} -- valid range numbers or null to clear the custom range
     * @param options
     * @returns {boolean} - true if plot numeric range has changed
     */
	setPlotSpecificMinMax( iRange, options ) {
		"use strict";
		var changed = false;

		if( this.numeric ) {
			changed = ! Dyn.mathUtil.equalRanges( this.numeric.plotRange, iRange );
			if( iRange ) {
                Dyn.assert( isFinite( iRange.min) && isFinite( iRange.max ));
				this.numeric.plotRange = new Dyn.mathUtil.Range( iRange.min, iRange.max );
				this.numeric.plotRange.normalize();
			} else {
				delete this.numeric.plotRange;
			}
			if( changed ) {
				this.needsCalcAxis = true; // see postCalcAxisUpdate()
			}
		} else {
			Dyn.assert( ! iRange, "cannot set range of non-numeric axis" );
		}
		return changed;
	}

    /**
     * get the major and minor steps for numeric axis tick marks,
     * as computed or attribute-metadata- or user-specified.
     * @returns {{majorStep: number, minorStep: number}}
     */
    getNumericAxisSteps() {
        return {
            majorStep: this.numeric?.majorMinor?.majorIntervalWidth || Math.NaN,
            minorStep: this.numeric?.majorMinor?.minorIntervalWidth || Math.NaN
        };
    }

    /**
     * Update the major and minor steps for numeric axis tick marks.
     * The formatting attribute will save these steps for future use, if they are valid values.
     * @param steps
     */
    updateNumericAxisSteps( steps, isUserDefined = true ) {
        let changed = false,
            formattingAttribute = this.getFormattingAttribute();
        Dyn.assert( formattingAttribute && this.coordMap && this.numeric.majorMinor );
        Dyn.assert( Dyn.MajorMinorWidth.isValidMajorWidth( steps.majorStep, this.coordMap, isUserDefined ) );
        Dyn.assert( Dyn.MajorMinorWidth.isValidMinorWidth( steps.minorStep, this.coordMap, isUserDefined ) );
        if( formattingAttribute && this.coordMap && this.numeric.majorMinor ) {
            // save changes with attribute formatting, then update our numeric majorMinor
            formattingAttribute?.setAxisMajorMinorWidth( steps.majorStep, steps.minorStep );
            this.numeric?.majorMinor?.update(
                formattingAttribute?.format.axisMajorWidth,
                formattingAttribute?.format.axisMinorWidth );
            this.needsCalcAxis = true; // see postCalcAxisUpdate()
            changed = true;
        }
        return changed;
    }

	showPlusMinusButtons( showPlusMinusButtons, disablePlusButton, disableMinusButton ) {
		"use strict";
		var oldSetting = this.isUsingPlusMinus;

		this.isUsingPlusMinus = Boolean(showPlusMinusButtons);
		if( this.isUsingPlusMinus !== oldSetting ) {
			this.needsCalcAxis = true; // see postCalcAxisUpdate()
		}
        this.plusButton?.getCoverElement().data("isEnabled", ! disablePlusButton );
        this.minusButton?.getCoverElement().data("isEnabled", ! disableMinusButton );
	}

	/**
	 * Used to set or reset a group of options, without having every plot know about every option.
	 * Example:
	 *   setPlotSpecificOptions({
	 * 		'plotMinMax':new Dyn.mathUtil.Range(0,100),
	 * 		'hideLinesLabels':true,
	 * 		'numActiveAttributes':0+,
	 * 		'showPlusMinus':true,
	 * 	    'isNonLinearMap':false
	 * 	    'showGridLines':false
     * 	    'wantFrequencyAxis':false
     * 	    'wantPercentAxis':false
     * 	    'isIncompleteAxis':false
	 * 		// NOTE: add new axis options here
	 * 	});
	 * @param options=null {*} --
	 */
	setPlotSpecificOptions( options ) {
		//console.log("AxisView.setPlotSpecificOptions()", this.axisID );
		if( ! options ) options = {};

        this.setStatsPending( false ); // temporarily clear to get old state
		let oldMarginForSum = this.marginForSum,
            oldHiddenSetting = this.isHiddenLineLabelsTicks,
            oldNumActiveAttributes = this.allAttributes.countActive(),
            oldNonLinearSetting = this.isNonLinearMap,
            oldFrequencyAxisSetting = this.isFrequencyAxis(),
            oldPercentAxisSetting = this.isPercentAxis(),
            oldProportionAxisSetting = this.isProportionAxis(),
            oldIsIncompleteAxisStyle = this.isIncompleteAxis();
        this.setStatsPending( true ); // set pending state until allAttributes is updated, below

		// add a margin of 0+ pixels after the upper range of a numeric axis,
        // for the "sum" text display, e.g. Bar Chart of Sums/Means
		this.marginForSum = options.marginForSum || 0;
		if( oldMarginForSum !== this.marginForSum ) {
			this.needsCalcAxis = true; // see postCalcAxisUpdate()
		}

        // hide the axis lines, tickmarks, and tick/bin labels?
        this.isHiddenLineLabelsTicks = Boolean(options.hideLinesLabels);
        if( this.isHiddenLineLabelsTicks !== oldHiddenSetting ) {
            this.needsCalcAxis = true; // see postCalcAxisUpdate()
        }

        // draw axis for non-linear map plot?
        this.isNonLinearMap = Boolean( options.isNonLinearMap );
        if( this.isNonLinearMap !== oldNonLinearSetting ) {
            this.needsCalcAxis = true; // see postCalcAxisUpdate()
        }

        // activate/deactivate the attributes
        let numActive = Dyn.mathUtil.isFinite( options.numActiveAttributes ) ?
                options.numActiveAttributes :
                this.allAttributes.count(); // default is all active
        this.allAttributes.setNumActive( numActive );
        if( this.allAttributes.countActive() !== oldNumActiveAttributes ) {
            this.needsCalcAxis = true; // see postCalcAxisUpdate()
        }

        // draw frequency, Percent, or Proportion axis for the plot?
        // note: to be combined with (numActiveAttributes:0 or null axis) and plotMinMax (to set the plot scale).
        this.axisScale.setFrequencyScale( Boolean(options.wantFrequencyAxis && this.allAttributes.countActive()===0 ));
        if( this.axisScale.hasFrequencyScale() !== oldFrequencyAxisSetting ) {
            this.needsCalcAxis = true; // see postCalcAxisUpdate()
        }
        this.axisScale.setPercentScale( Boolean(options.wantPercentAxis ));
        if( this.axisScale.hasPercentScale() !== oldPercentAxisSetting ) {
            this.needsCalcAxis = true; // see postCalcAxisUpdate()
        }
        this.axisScale.setProportionScale( Boolean(options.wantProportionAxis ));
        if( this.axisScale.hasProportionScale() !== oldProportionAxisSetting ) {
            this.needsCalcAxis = true; // see postCalcAxisUpdate()
        }

        this.setIncompleteAxis( options.isIncompleteAxisStyle );
        if( this.isIncompleteAxisStyle !== oldIsIncompleteAxisStyle ) {
            this.needsCalcAxis = true; // see postCalcAxisUpdate()
        }

        this.setMarginForButtons( options.marginForButtons );
		this.setPlotSpecificMinMax( options.plotMinMax );
		this.showPlusMinusButtons( options.showPlusMinus, options.disablePlusButton, options.disableMinusButton );
		this.showHideGridLines( options.showGridLines );

		// add new plot-specific options here, plots that don't use them will automatically clear the new option.

        this.setStatsPending( false );
	}

    /**
     * Set the margin on the Y axis so the axis title does not overlap with buttons on Y axis.
     * Should be called by plot elements which create buttons, during PlotElement.calcAxis()
     * @param marginSize -- 0+ or undefined
     */
    setMarginForButtons( buttonSizeInY ) {
        let oldMarginForButtons = this.marginForButtons;
        this.marginForButtons = buttonSizeInY || 0;
        if( oldMarginForButtons !== this.marginForButtons ) {
            //console.log("setMarginForButtons", this.marginForButtons );
            this.needsSetTitle = true; // see postCalcAxisUpdate()
        }
    }

    /**
     * Show or Hide the Grid Lines.
     * Must be called before postCalcAxisUpdate(), typically in a PlotElement.calcAxis() override function.
     * @param wantGridLines
     */
    showHideGridLines( wantGridLines ) {
        "use strict";
        let oldShowGridLines = this.showGridLines;
        this.showGridLines = Boolean( wantGridLines );
        if( oldShowGridLines !== this.showGridLines ) {
            this.needsCalcAxis = true; // see postCalcAxisUpdate()
        }
    }

	/**
	 * Update this axis after customizations set by Dyn.PlotElement.CalcAxis().
	 * To be called by Dyn.PlotShaper.UpdateAxes()
	 */
	postCalcAxisUpdate() {
		"use strict";
		// check attribute stats to see if they are out of date
        //console.log("AxisView:postCalcAxisUpdate() on "+(this.isHorizontal?"X":"Y"));
		var	statsUpdated = this.allAttributes.updateStatsIfDataChange(),
            attributeTitleUpdated = ( this.titleString !== this.allAttributes.name && ! this.isNullAxis() );
        if( attributeTitleUpdated ) {
            this.setTitle( this.allAttributes.name ); // title has changed along with data
            this.needsSetTitle = true;
        }

		this.needsCalcAxis = Boolean( this.needsCalcAxis || statsUpdated );
		if( this.needsCalcAxis ) {
			this.updateAxisTypeAndThickness(); // adjust this.numeric.axisRange and all dependent parameters
			this.needsCalcAxis = false;
		}
		if( this.needsSetTitle ) {
            this.updateDropZone();
            this.updateTitles();
			this.needsSetTitle = false;
		}

		// Update the case card view based on selection
		if ( this.getAttribute() ){
			$.each( this.allAttributes.attributes, function( index, value ) {
				mediator.publish( "records:setAddedAttribute", value );
			});
		}
	}

	/**
	 * Highlight the drop area if compatible with the dragged object.
     * Called at the start of something being dragged, but not yet over this axis.
     * See also:
     *      mouseOverDroppableAreas() -- stronger highlight when mouse is over this axis
     *      resetHighlightDropAreas() -- undo highlightDropAreas()
     * @param draggedObject {*}
     * @param [deltaThickness] {null|{left,right,top,bottom}}
     */
	highlightDropAreas( draggedObject, deltaThickness=null ){
		// if the thing is an attribute, highlight our drop area.
        let dropState = "isHighlight",
		    draggedAttribute = ((draggedObject instanceof Dyn.Attribute ) ? draggedObject : null),
            highlightColors = {
                stroke: Dyn.plotUtil.highlightDropStrokeColor,
                "stroke-opacity": 1,
                "stroke-width": Dyn.plotUtil.highlightDropRectStrokeWidth,
                fill: this.getDropRectFillColor( dropState ),
                'fill-opacity': this.getDropRectFillOpacity( dropState )
            },
            buttons = [this.plusButton, this.minusButton, this.settingsRect];

		if( draggedAttribute ) {
		    // expand the drop area while dragging only
		    if( ! this.dropRect.data("isDragExpanded") ) {
                let x = this.dropRect.attr("x"),
                    y = this.dropRect.attr("y"),
                    oldHeight = this.dropRect.attr("height"),
                    oldWidth = this.dropRect.attr("width");

                if( this.isHorizontal ) {   // expand x-axis top of rect
		            let newHeight = Math.max( oldHeight, Dyn.plotUtil.dropExpandMin ),
                        deltaHeight = (newHeight-oldHeight);
                    if( deltaThickness && (deltaThickness.left > 0 || deltaThickness.right > 0)) {
                        let newWidth = Math.max(0, oldWidth - (deltaThickness.left+deltaThickness.right));
                        this.dropRect.attr({ y:y-deltaHeight, height:newHeight, x:x+deltaThickness.left, width:newWidth });
                    } else {
                        this.dropRect.attr({ y:y-deltaHeight, height:newHeight });
                    }
                    shiftButtonsAndTitles( buttons, this.titles, 0, -deltaHeight/2 );
                } else {                    // expand y-axis right of rect
                    let newWidth = Math.max( oldWidth, Dyn.plotUtil.dropExpandMin ),
                        deltaWidth = (newWidth-oldWidth);
                    if( this.axisID==='y') {
                        this.dropRect.attr({ width:newWidth });
                        shiftButtonsAndTitles( buttons, this.titles, deltaWidth/2, 0, true );
                    } else { // this.axisID==='y2'
                        let shiftX = ( deltaThickness && (deltaThickness.right > 0)) ? -deltaThickness.right : 0;
                        this.dropRect.attr({ width:newWidth, x:x - deltaWidth + shiftX });
                        shiftButtonsAndTitles( buttons, this.titles, -deltaWidth/2 + shiftX, 0, true );
                    }
                    if( deltaThickness && deltaThickness.bottom > 0) {
                        this.dropRect.attr({ "height": oldHeight - deltaThickness.bottom });
                    }
                }
                this.dropRect.data( "isDragExpanded", true );
            }

			this.dropRect.attr( highlightColors );
			if( this.allowAnotherAttribute( draggedAttribute )) {
                highlightColors.stroke = Dyn.plotUtil.highlightDropStrokeColor;
                highlightColors.fill = Dyn.plotUtil.highlightDropFillColor;
                highlightColors['fill-opacity'] = 1;
                this.extraDropRect.attr( highlightColors ).show();
				this.extraDropText.show();
			} else {
                this.extraDropRect.hide();
				this.extraDropText.hide();
			}

			// disable mouseover and clicks
			this.enableDisableForDragDrop( false );
		}

        function shiftButtonsAndTitles( buttons, titles, dx, dy, rotatedText ) {
            if(( dx !== 0 ) || (dy !== 0)) {
                titles.forEach( (title) => {
                    Dyn.axisUtil.shiftTitle( title.text, title.bbox, dx, dy, rotatedText );
                    Dyn.axisUtil.shiftButton( title.closeBox, dx, dy );
                    Dyn.axisUtil.shiftPath( title.colorRect, dx, dy );
                });
                buttons.forEach((b) => Dyn.axisUtil.shiftButton( b, dx, dy ));
            }
        }
	}

	/**
	 * Undo highlightDropAreas() changes.
	 */
	resetHighlightDropAreas(){
		"use strict";
		// to support maps have transparent strokeColor for axis border.
		var //dropStrokeColor = Dyn.plotUtil.transparentContainer,
            enableExtraDropZone = this.allowAnotherAttribute();

		// undo highlightDropAreas' visual changes to drop zone
        this.removeGlow();
        this.dropRect.attr(  {
			stroke: Dyn.plotUtil.promptDropStrokeColor, //dropStrokeColor,/* "stroke-opacity": 1,*/
            "stroke-opacity": this.getDropRectStrokeOpacity(),
			"stroke-width": Dyn.plotUtil.promptDropRectStrokeWidth,
			fill: this.getDropRectFillColor(),
			'fill-opacity': this.getDropRectFillOpacity()
        });
        if( enableExtraDropZone ) {
            this.extraDropRect.attr( {
                stroke: Dyn.plotUtil.promptDropStrokeColor,
                "stroke-opacity": 1,
                "stroke-width": Dyn.plotUtil.promptDropRectStrokeWidth,
                fill: Dyn.plotUtil.promptDropFillColor,
                'fill-opacity': 1
            } ).show();
            this.extraDropText.show();
        } else {
            this.extraDropRect.hide();
            this.extraDropText.hide();
        }

		this.enableDisableForDragDrop( true ); // re-enable mouseover and clicks
        this.updateDropZone(); // undo "isDragExpanded" boundaries
        this.updateTitles();   // undo "isDragExpanded" text shift
        this.dropRect.data( "isDragExpanded", false ); // forget custom setting
	}

    /**
     * Get the drop zone fill color, with optional dropState
     * @param [dropState] -- optional state of drop, if any
     * @return {*} - fill color hex value
     */
    getDropRectFillColor( dropState ) {
        Dyn.DropState.isValid( dropState );
        // NOTE: more than one flag can be set, we check in priority order
        if( dropState==="isOverDrop" ) {
            return Dyn.plotUtil.overDropFillColor;
        // } else if( dropState==="isDropElsewhere" ) {
        //     return Dyn.plotUtil.dropElsewhereFillColor;
        } else if( this.isIncompleteAxis() ) {
            return Dyn.plotUtil.warningDropFillColor;
        } else if( dropState==="isHighlight" ){
            return Dyn.plotUtil.highlightDropFillColor;
        } else if( this.isUsingPromptTitle ) {
            return Dyn.plotUtil.promptDropFillColor;
        } else {
            return Dyn.plotUtil.normalDropColor;
        }
    }

    getDropRectFillOpacity( dropState ) {
        Dyn.DropState.isValid( dropState );
        if( Dyn.DropState.isInDrop( dropState ) || this.isUsingPromptTitle || this.isIncompleteAxis() ) {
            return 1; // obscure background when dragging attributes or showing prompt
        } else if( this.attribute ) {
            return Dyn.plotUtil.kLegendOpacity; // show map behind legend
        } else {
            return 0; // invisible if no legend or prompt, especially for map plot
        }
    }

    getDropRectStrokeOpacity( dropState ) {
        Dyn.DropState.isValid( dropState );
        if( dropState==="isOverDrop" || this.isUsingPromptTitle ) {
            // show stroke with prompt axis or droppable attribute over drop area
            return 1;
        } else {
            // hide stroke if axis in use, or empty, or this.isIncompleteAxis()
            return 0;
        }
    }

	/**
	 * Enable or Disable mouse over all controls not used during drag and drop.
	 * Used to disable then re-enable during attribute drags.
	 * @param isEnable {boolean)
	 */
	enableDisableForDragDrop( isEnable ) {
		// enable or disable all controls not used during drag and drop
		this.titles.forEach((value) => {
			if( value.closeBox )
				enableDisable( value.closeBox.getCoverElement(), isEnable ); // disable cover of closebox set, see Dyn.axisUtil.makeCloseBox()
			if( value.text ) {
			    let isNotMathJax = ! Boolean( value.text.data( "isMathJax" ));
                enableDisable( value.text, isEnable && isNotMathJax );
            }
		});
		this.labels.forEach((value) => {
			enableDisable( value, isEnable );
		});
		enableDisable( this.plusButton.getCoverElement(), isEnable );
		enableDisable( this.minusButton.getCoverElement(), isEnable );
        enableDisable( this.settingsRect.getCoverElement(), isEnable );
		enableDisable( this.dragMiddle.getCoverElement(), isEnable );
		enableDisable( this.dragMin.getCoverElement(), isEnable );
		enableDisable( this.dragMax.getCoverElement(), isEnable );

		function enableDisable( element, isEnable ) {
			// ignore null elements, since not all elements are used in every axis
			if( element ) {
				if( isEnable ) {
					Dyn.plotUtil.enableMouseOverEvents( element );
				} else {
					Dyn.plotUtil.disableMouseOverEvents( element );
				}
			}
		}
	}

	/**
	 * Respond to something being dragged, and now over one of our drop areas.
	 */
	mouseOverDroppableAreas( draggedObject, isExtraDropRect, event, isOverOtherAxis ){
		// if the draggedObject is an attribute but not one of ours, highlight our drop area
        let dropState = "isOverDrop",
            draggedAttribute = ((draggedObject instanceof Dyn.Attribute ) ? draggedObject : null),
			overColors = {
				fill: this.getDropRectFillColor( dropState ),
				'fill-opacity': this.getDropRectFillOpacity( dropState ),
				'stroke-opacity': this.getDropRectStrokeOpacity( dropState )
			},
            x, y;

		if( ! draggedAttribute ) {
            return; // nothing to do
        }
        if( ! isOverOtherAxis ){
            let allowAnother = this.allowAnotherAttribute( draggedAttribute ),
                thisElement = isExtraDropRect ? this.extraDropRect : this.dropRect,
                showTip = Dyn.plotUtil.isElementVisible( thisElement ) && // don't show on extraDropRect if hidden
                    !this.isNullAxis(); // don't show on dropRect if no element to replace

            if( isExtraDropRect ){
                if( allowAnother ){
                    this.addGlow( this.extraDropRect );
                    this.extraDropRect.attr( overColors );
                    x = this.extraDropRect.attrs.x;
                    y = this.extraDropRect.attrs.y;
                } else {
                    showTip = false;
                }
            } else {
                this.addGlow( this.dropRect );
                this.dropRect.attr( overColors );
                x = this.dropRect.attrs.x;
                y = this.dropRect.attrs.y;
            }
            if( showTip ){
                var options = { x: x, y: y, alignBottom: true };
                if( Dyn.toolTip.isMyElement( thisElement ) ){
                    Dyn.toolTip.update( thisElement, event, options );
                } else {
                    Dyn.toolTip.show( thisElement, event, options );
                }
            }
        }
	}

    // mouseOverOtherDropArea() {
    //
    // }

	/**
	 * Respond to something being dragged, and no longer on this axis.
	 */
	mouseOutDroppableAreas( draggedObject, isExtraDropRect /*, e */){
		this.highlightDropAreas( draggedObject ); // go back to usual highlight state
		var thisElement = isExtraDropRect? this.extraDropRect: this.dropRect;
		if( Dyn.toolTip.isMyElement( thisElement ))
			Dyn.toolTip.hide();
        this.removeGlow();
	}

    /** respond to mouse up over one of our droppable areas */
    mouseUpDroppableAreas( draggedObject, isExtraDropRect, event ) {
        var thisElement = isExtraDropRect? this.extraDropRect: this.dropRect;
        if( Dyn.toolTip.isMyElement( thisElement ))
            Dyn.toolTip.hide();
        this.removeGlow();
    }

    /**
     * change the dataVal to the nearest tick mark if close enough, to snap to grid
     * @param dataVal
     * @param snapDistanceInPixels
     * @returns {*}
     */
    snapIfCloseToTickmark( dataVal, snapDistanceInPixels ) {
        let result = dataVal;
        if( this.numeric && this.numeric.snapIntervalWidth ) {
            let w = this.numeric.snapIntervalWidth,
                d = this.coordMap.inverseScaleAbs( snapDistanceInPixels ),
                n = Math.round( dataVal / w ) * w;
            if( Dyn.mathUtil.isClose( dataVal, n, d )) {
                result = n;
            }
        }
        return result;
    }

    addGlow( elementToGlow ) {
        "use strict";
        var width = Dyn.plotUtil.dropGlowWidth,
            glowColor = elementToGlow.attr("stroke");
        this.removeGlow(); // remove previous glow, if any
        this.glowElement = elementToGlow.glow( {
            color: glowColor,
            width: width
        });
    }

    /**
     *  Remove all visual elements created by this axis, undoing initVisuals(), updateVisuals().
     *  Compare to initVisuals, layerVisuals(), and constructor for the elements to handle.
     */
    removeVisuals() {
        this.dropRect.remove();         this.dropRect = null;
        this.extraDropRect.remove();    this.extraDropRect = null;
        this.extraDropText.remove();    this.extraDropText = null;

        this.removeTitles();
        this.removeLine();
        this.removeGlow();

        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.binLines );
        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.ticks );
        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.minorLines );
        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.originLine );
        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.labelSelectRects );
        Dyn.plotUtil.removeAndEmptyArrayOfElements( this.labels );

        this.plusButton.remove();   this.plusButton = null;
        this.minusButton.remove();  this.minusButton = null;
        this.dragMiddle.remove();   this.dragMiddle = null;
        this.dragMax.remove();      this.dragMax = null;
        this.dragMin.remove();      this.dragMin = null;
        this.settingsRect.remove(); this.settingsRect = null;
    }

    /**
     * Remove the glow element created by addGlow()
     */
    removeGlow() {
        if( this.glowElement ) {
            this.glowElement.remove();
            this.glowElement = null;
        }
    }

    removeTitles() {
        function removeTitle( title ) {
            title.text.remove();
            title.closeBox.remove();
            title.colorRect.remove();
        }
        this.titles.forEach( (el) => removeTitle(el) );
        this.titles.length = 0; // empty the array
    }

    removeLine() {
        if( this.line ) {
            this.line.remove();
            this.line = null;
        }
    }

    setCrossAxisRef( crossAxisView, legendView=null, iconStyler=null ) {
        class AxisViewRefs {
            // API for read-only access to cross axis and legend and icon sizes by this axis
            constructor( crossAxisView, legendView ) {
                Dyn.assert( crossAxisView );
                Dyn.assert( this.isHorizontal !== crossAxisView?.isHorizontal, "cross axis must be opposite orientation" );
                this.crossAxisView = crossAxisView; // Dyn.AxisView or null
                this.legendView = legendView; // Dyn.RightMarginView or null
                this.iconStyler = iconStyler; // Dyn.IconStyler or null
            }
            get crossAxisBounds() {
                return this.crossAxisView.bounds;
            }
            get crossAxisMargin() {
                return this.crossAxisView.margin;
            }
            get hasColorAxis() {
                return Boolean( this.legendView?.isColorAxis() );
            }
            getCustomIconSize() {
                return this.iconStyler?.getCustomIconSize() || 0;
            }
            get crossAxisIsNumericalScaleAxis() {
                return this.crossAxisView.isNumericalScaleAxis();
            }
        }
        this.viewRefs = new AxisViewRefs( crossAxisView, legendView );
    }

    /**
     * set or clear the reference to our 2nd axis, if any, so they can function as dual axes
     * */
    setDualAttributesRef( secondAxisView ) {
        // clear old cross-references
        if( this.secondAxis ) {  // clear old attribute set cross-reference, if any
            this.secondAxis.setFirstAxis( null );
            this.secondAxis = null;
            this.allAttributes.setDualAttributes( null );
        }

        // save a reference to our 2nd axis
        this.secondAxis = secondAxisView ? secondAxisView : null;
        if( this.secondAxis ){
            this.secondAxis.setFirstAxis( this );
            this.allAttributes.setDualAttributes(this.secondAxis.allAttributes);
        }
    }

    /**
     * Test our axis to see if it is the 1st of a dual axis plot.
     * Generally used to test the Y axis to see if has a Y2 axis.
     */
    isFirstOfDualAxis() {
        return Boolean( this.secondAxis );
    }

    /**
     * @param wantPending {boolean}
     */
    setStatsPending( wantPending ) {
        this.allAttributes.setStatsPending( wantPending );
    }

    checkStatsPending() {
        this.allAttributes.checkStatsPending();
    }

    // constants (see also Dyn.plotUtil.kAxisLineColor, etc)
    static kTickLength = 6;

}   // end class AxisView
Dyn.AxisView = AxisView;

    // constants (see also Dyn.plotUtil.kAxisLineColor, etc)
    Dyn.AxisView.prototype.kMinorTickLength = 2; // length of minor tick marks
    Dyn.AxisView.prototype.kMinDistMinorTicks = 10; // at least 10 pixels between minor ticks, otherwise don't display
    Dyn.AxisView.prototype.kTickLabelSpacer = 2;
    Dyn.AxisView.prototype.kLineOffset		= 3; // distance from inner edge of axis view to center of axis line
    //Dyn.AxisView.prototype.kCloseBoxOffset = 15; 	// offset from bottom of axis to align with text label
    Dyn.AxisView.prototype.kColorSquareHalfSize = 5;
    Dyn.AxisView.prototype.kColorSquareOffset = 12;
    Dyn.AxisView.prototype.kSpaceBetweenLabels = 6; // minimum number of pixels between adjacent labels (otherwise rotate to get more space)
    Dyn.AxisView.prototype.kDragButtonHeight = 6;
    Dyn.AxisView.prototype.kDragButtonWidth = 20;
    Dyn.AxisView.prototype.kDragMinFract= 0.03; // center at 0.05 times axis length, from start
    Dyn.AxisView.prototype.kDragMaxFract= 0.98; // center at 0.95 times axis length, from start
    //Dyn.AxisView.prototype.kTitleButtonXOffset = 2; //adjust button on X-axis downward 2 pixels on X-axis to align with text

    class Y2AxisView extends AxisView {

        /**
         * Dyn.Y2AxisView()
         * Parent class is Dyn.AxisView
         * @constructor
         */
        constructor( w, h, plotPaper ) {
            super( false, w, h, plotPaper );

            // overrides of Dyn.AxisView
            this.bounds = new Dyn.mathUtil.Rect( 0, 0, w, h );
            this.isHorizontal = false;
            this.axisID = 'y2';

            // allocate new subobjects (to get around bug in .inheritsFrom())
            this.allAttributes = new Dyn.CombinedAttributes( false );
            this.numeric = {};

            // reference to Y1 axis
            this.firstAxis = null;

            // Raphael elements
            this.paper = plotPaper;
        }

        //Dyn.Y2AxisView.inheritsFrom( Dyn.AxisView );

        /** {OVERRIDE} to mirror placement of text on Y2 axis, compared to Y axis */
        getLabelTextTransform( textElem ) {
            "use strict";
            let t;
            if( this.labelInfo.wantRotation ) {
                // Y2 axis vertical text, tick mark is on left center edge of text
                t = Dyn.plotUtil.getTextTransform( textElem, "r-90,left,center" );
            } else {
                // Y2 axis horizontal text, tick mark is on left center edge of text
                t = Dyn.plotUtil.getTextTransform( textElem, "left,center" );
            }
            return t;
        }

        static #isAcceptingMultipleAttributes = false; // set to true to enable

        /** {OVERRIDE} to disable multiple attributes and extra drop zone for Y2 axis */
        allowAnotherAttribute( optionalNewAttribute ) {
            return Y2AxisView.#isAcceptingMultipleAttributes &&
                super.allowAnotherAttribute( optionalNewAttribute );
        }

        setFirstAxis( otherAxisView ) {
            this.firstAxis = otherAxisView; // Dyn.AxisView or null
        }

    }   // end class Y2AxisView
    Dyn.Y2AxisView = Y2AxisView;

}; // wrapDynAxisView
